import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePermissions } from "@/contexts/PermissionsContext";
import SccmImportDialog from "@/components/SccmImportDialog";
import axios from "axios";
import { toast } from "sonner";
import CVEManagementPage from "@/pages/CVEManagementPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  RefreshCw,
  Monitor,
  Package,
  Rocket,
  LogOut,
  Book,
  Layers,
  HardDrive,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Settings,
  Download,
  X,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  BarChart3,
  Filter,
  Database,
  ShieldAlert,
  Shield,
  Bug,
  ArrowUpCircle,
  ExternalLink,
  Upload,
  Loader2,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ban,
  Eye,
  EyeOff,
  Recycle,
  Zap,
  ChevronDown,
  Trash2,
} from "lucide-react";

// Deployment pipelines: a rollout has 1-10 stages after UAT, each a named
// Entra group ("Pilot") or a percentage sample. Name and label a stage from
// a phased deployment's phases.phaseN entry (or a go-live-phased response
// entry); "Phase N" only when the entry carries no name (pre-pipeline data).
const isNamedGroupStage = (p) => !!p && (p.predefined === true || p.stage_type === "group");
const stageName = (p, n) =>
  (isNamedGroupStage(p) ? (p?.name || p?.group_name) : p?.name) || `Phase ${n}`;
const stageLabel = (p, n) =>
  !isNamedGroupStage(p) && p?.percentage != null ? `${stageName(p, n)} (${p.percentage}%)` : stageName(p, n);
// Family key for the stage-name cache below: the family plus the package
// being rolled out, so a new version (possibly a different pipeline) refetches.
const familyStageKey = (family) =>
  `${family.family_name || family.display_name_normalized || family.display_name}|${family.packaging_status?.package_id || ""}`;

// Step indicator component for sync overlay
const StepIndicator = ({ label, isActive, isComplete }) => (
  <div className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
    isActive ? 'bg-cyan-500/20 border border-cyan-500/50' : 
    isComplete ? 'bg-green-500/10 border border-green-500/30' : 
    'bg-slate-800/30 border border-slate-700/50'
  }`}>
    {isComplete ? (
      <CheckCircle2 className="h-4 w-4 text-green-400" />
    ) : isActive ? (
      <Loader2 className="h-4 w-4 text-cyan-400 animate-spin" />
    ) : (
      <div className="h-4 w-4 rounded-full border-2 border-slate-600" />
    )}
    <span className={`text-xs font-medium ${
      isActive ? 'text-cyan-300' : 
      isComplete ? 'text-green-400' : 
      'text-white/70'
    }`}>
      {label}
    </span>
  </div>
);

// Split action button for the Rationalised Applications table.
//
// The primary half keeps whatever packaging action the row already had
// (Package from winget, Package from the Private Repository, or Custom
// upload). The caret opens the destructive alternative: build an
// "Uninstall only" package that removes the application from the estate
// instead of deploying it.
//
// Uninstall only sits behind a deliberate second click rather than beside
// the primary button because a mis-click on this row would queue a
// fleet-wide removal — the confirmation dialog is the second gate.
const PackageSplitButton = ({
  label,
  icon: PrimaryIcon,
  primaryClassName,
  caretClassName,
  onPrimary,
  onUninstallOnly,
  disabled,
  title,
  primaryMenuLabel,
  testId,
  idx,
}) => (
  <div className="inline-flex items-stretch align-middle">
    <Button
      variant="outline"
      size="sm"
      onClick={onPrimary}
      className={`${primaryClassName} rounded-r-none font-semibold text-xs`}
      data-testid={testId}
      disabled={disabled}
      title={title}
    >
      <PrimaryIcon className="h-3 w-3 mr-1" />
      {label}
    </Button>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`${caretClassName} rounded-l-none border-l border-black/20 px-1.5 font-semibold text-xs`}
          data-testid={`package-options-${idx}`}
          disabled={disabled}
          title={disabled ? title : "More packaging options"}
          aria-label="More packaging options"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-[#0a1929] border-white/10">
        <DropdownMenuItem
          onClick={onPrimary}
          className="cursor-pointer text-white hover:bg-white/5 focus:bg-white/5"
          data-testid={`package-install-option-${idx}`}
        >
          <Package className="h-4 w-4 mr-2" />
          {primaryMenuLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onUninstallOnly}
          className="cursor-pointer text-red-300 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-200"
          data-testid={`uninstall-only-option-${idx}`}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Uninstall only
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

const DiscoverPage = () => {
  const { user, logout, getAccessToken } = useAuth();
  const { canCreatePackages, canDeployPackages, canManageApps, isViewer, hasCapability } = usePermissions();
  // An SCCM-mode tenant has no Graph connection: its estate arrives as a
  // CSV import rather than an Intune sync, so the two are mutually
  // exclusive by capability.
  const canSyncIntune = hasCapability("intune_sync");
  const canImportSccm = hasCapability("sccm_import");
  const [sccmImportOpen, setSccmImportOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // "Back to Discover" from the Apps Governance page passes this flag so
  // we can re-run the packaging-status sync (matching the initial
  // page-load behavior) before the Rationalised Applications table is
  // displayed again. Without this, users see stale `actions` values that
  // were computed before their most recent governance-page changes.
  //
  // Snapshot the flag into a ref on the very first render so:
  //   1. React 18 strict-mode double-invokes don't see it as `false` on
  //      the second render (we clear the router state below).
  //   2. Downstream effects can rely on a stable value that matches what
  //      the boot effect observed.
  const backFromGovernanceSyncRef = useRef(location.state?.syncOnMount === true);
  // Consume the location state immediately so a subsequent re-render, a
  // browser back/forward, or a full refresh doesn't accidentally
  // re-trigger the sync. Using history.replaceState (rather than
  // navigate({state:null})) avoids an extra router round-trip on mount.
  if (backFromGovernanceSyncRef.current && location.state?.syncOnMount) {
    try {
      window.history.replaceState({}, "", window.location.href);
    } catch (_) {
      // No-op: some sandboxed environments disallow history writes.
    }
  }
  
  // Get initial view from URL params (for back navigation)
  const initialView = searchParams.get("view") || "rationalisation";
  
  // Scroll restoration refs and state
  const scrollRestorationKey = "discover_scroll_state";
  const hasRestoredScroll = useRef(false);
  const contentRef = useRef(null);
  
  // State
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  // True when the most recent /discover/stats fetch failed AND we have
  // no usable cached value to display. Lets the tiles render a "—"
  // placeholder instead of a misleading "0" / "Never". At large tenant
  // scale (7k+ apps) Cosmos can throttle the underlying aggregations.
  const [statsError, setStatsError] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // First-load gate. Blocks the rationalisation table + category filters
  // until the silent deployment-status sync completes, so users can't
  // click into "Unmanaged" while the data is still being reconciled.
  const [initialSyncing, setInitialSyncing] = useState(true);
  const [search, setSearch] = useState(() => {
    // Restore search from sessionStorage if returning to page
    const saved = sessionStorage.getItem(scrollRestorationKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.search || "";
      } catch { return ""; }
    }
    return "";
  });
  const [page, setPage] = useState(() => {
    // Restore page from sessionStorage if returning to page
    const saved = sessionStorage.getItem(scrollRestorationKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.page || 1;
      } catch { return 1; }
    }
    return 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState(initialView);
  const [families, setFamilies] = useState([]);
  // Stage names for families mid-rollout (packaging status live_phaseN),
  // keyed by familyStageKey: { names: [...], count }. The families list
  // carries only the stage number, so the names come from go-live-by-app.
  const [liveStageInfo, setLiveStageInfo] = useState({});
  const requestedStageKeys = useRef(new Set());
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [rationalisation, setRationalisation] = useState(null);
  const [appsDbStats, setAppsDbStats] = useState(null);
  const [cveDbStats, setCveDbStats] = useState(null);
  const [vulnerableApps, setVulnerableApps] = useState([]);
  const [vulnStats, setVulnStats] = useState(null);
  const [wingetDbStats, setWingetDbStats] = useState(null);
  const [upgradableApps, setUpgradableApps] = useState([]);
  const [upgradeStats, setUpgradeStats] = useState(null);
  
  // Placeholder for removed consolidation section
  
  // CVE Details Modal state
  const [showCveModal, setShowCveModal] = useState(false);
  const [selectedCveApp, setSelectedCveApp] = useState(null);
  
  // App Category filter state
  const [appCategories, setAppCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    // Restore category from sessionStorage if returning to page
    const saved = sessionStorage.getItem(scrollRestorationKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.selectedCategory || "";
      } catch { return ""; }
    }
    return "";
  });
  
  // Test approval state
  const [showGoLiveWarning, setShowGoLiveWarning] = useState(false);
  const [pendingGoLiveItem, setPendingGoLiveItem] = useState(null);
  const [testApprovals, setTestApprovals] = useState({});
  // Family awaiting "Uninstall only" confirmation, and the in-flight guard
  // that stops a double-click creating two packages.
  const [uninstallOnlyTarget, setUninstallOnlyTarget] = useState(null);
  const [uninstallOnlyBusy, setUninstallOnlyBusy] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    excludeSystem: true,
    excludeDrivers: true,
    excludeNonBusiness: true,
    excludeRedistributable: true,
    excludeHelpFiles: true,
    excludeDeprecated: true,
    excludeInstallationFiles: true,
    excludeBundle: true,
  });
  const [filtersEnabled, setFiltersEnabled] = useState(false);
  // Aborts the previous in-flight call when a new one fires for the same
  // endpoint. Without these, multiple useEffects (boot, [viewMode,
  // filtersEnabled], [debouncedSearch], [filters]) all kick fetches at
  // mount and on every dep change — Cosmos sees 3-4 concurrent requests
  // for the same data, each consuming RUs.
  const rationalisationAbortRef = useRef(null);
  const familiesAbortRef = useRef(null);
  const filteredFamiliesAbortRef = useRef(null);
  const managementCountsAbortRef = useRef(null);
  
  // Sorting state for rationalisation table
  const [sortConfig, setSortConfig] = useState({
    key: 'device_count', // Default sort by device count
    direction: 'desc'    // Default descending (highest first)
  });
  
  // Management filter state (All / Managed / Unmanaged / Out of Scope)
  const [managementFilter, setManagementFilter] = useState("all");
  const [managementCounts, setManagementCounts] = useState({ all: 0, managed: 0, unmanaged: 0, out_of_scope: 0 });
  
  const PAGE_SIZE = 50;
  
  // Debounced search state - to prevent too many API calls when typing fast
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const searchDebounceRef = useRef(null);
  
  // Update debounced search with delay
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300); // 300ms debounce delay
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);
  
  // Sorting function for families/rationalisation table
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Get sorted families
  const getSortedFamilies = useCallback(() => {
    if (!families || families.length === 0) return [];
    
    const sorted = [...families].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'family_name':
          aVal = (a.family_name || '').toLowerCase();
          bVal = (b.family_name || '').toLowerCase();
          break;
        case 'device_count':
          aVal = a.unique_device_count || a.total_device_count || 0;
          bVal = b.unique_device_count || b.total_device_count || 0;
          break;
        case 'version_count':
          aVal = a.versions?.length || a.version_count || 0;
          bVal = b.versions?.length || b.version_count || 0;
          break;
        case 'status':
          // Sort by status priority: live > approved > deployed_test > packaged > automation > manual
          const statusPriority = (status, winget) => {
            if (status === 'live') return 6;
            if (status === 'approved') return 5;
            if (status === 'deployed_test') return 4;
            if (status === 'packaged') return 3;
            if (winget) return 2;
            return 1;
          };
          aVal = statusPriority(a.packaging_status?.status, a.winget_matched);
          bVal = statusPriority(b.packaging_status?.status, b.winget_matched);
          break;
        case 'category':
          aVal = (a.category || 'zzz').toLowerCase(); // Put empty categories last
          bVal = (b.category || 'zzz').toLowerCase();
          break;
        case 'criticality':
          const critPriority = { critical: 4, high: 3, medium: 2, low: 1 };
          aVal = critPriority[a.risk_level?.toLowerCase()] || 0;
          bVal = critPriority[b.risk_level?.toLowerCase()] || 0;
          break;
        default:
          aVal = a[sortConfig.key] || 0;
          bVal = b[sortConfig.key] || 0;
      }
      
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [families, sortConfig]);
  
  // SortableHeader component
  const SortableHeader = ({ column, label, className = "" }) => {
    const isActive = sortConfig.key === column;
    return (
      <TableHead 
        className={`text-white font-semibold cursor-pointer hover:bg-white/[0.04] select-none ${className}`}
        onClick={() => handleSort(column)}
        data-testid={`sort-${column}`}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortConfig.direction === 'asc' ? (
              <ArrowUp className="h-3 w-3 text-white" />
            ) : (
              <ArrowDown className="h-3 w-3 text-white" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3 text-white/70" />
          )}
        </div>
      </TableHead>
    );
  };
  
  // Save scroll state before navigating away
  const saveScrollState = useCallback(() => {
    const scrollState = {
      scrollY: window.scrollY,
      search,
      page,
      viewMode,
      selectedCategory,
      timestamp: Date.now()
    };
    sessionStorage.setItem(scrollRestorationKey, JSON.stringify(scrollState));
  }, [search, page, viewMode, selectedCategory]);
  
  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && !hasRestoredScroll.current) {
      const saved = sessionStorage.getItem(scrollRestorationKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Only restore if saved within last 5 minutes (to avoid stale state)
          if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
              window.scrollTo({ top: parsed.scrollY || 0, behavior: "instant" });
            }, 100);
          }
          // Clear the saved state after restoration
          sessionStorage.removeItem(scrollRestorationKey);
        } catch (e) {
          console.warn("Failed to restore scroll position:", e);
        }
      }
      hasRestoredScroll.current = true;
    }
  }, [loading]);
  
  // Navigate to governance page with scroll state preservation
  const navigateToGovernance = useCallback((familyName) => {
    saveScrollState();
    navigate(`/discover/governance/${encodeURIComponent(familyName)}?from=${viewMode}`);
  }, [navigate, viewMode, saveScrollState]);
  
  // Fetch detected apps - accepts page as parameter
  const fetchApps = useCallback(async (pageNum = page) => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/detected-apps`, {
        params: { search: debouncedSearch, page: pageNum, page_size: PAGE_SIZE },
        headers
      });
      
      setApps(response.data.apps || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Error fetching apps:", error);
      toast.error("Failed to fetch detected apps");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, getAccessToken, page]);
  
  // Fetch app categories for filter dropdown
  const fetchCategories = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/app-categories`, { headers });
      setAppCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [getAccessToken]);
  
  // Fetch management counts for toggle badges
  const fetchManagementCounts = useCallback(async () => {
    if (managementCountsAbortRef.current) managementCountsAbortRef.current.abort();
    const controller = new AbortController();
    managementCountsAbortRef.current = controller;
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/discover/management-counts`, { headers, signal: controller.signal });
      if (managementCountsAbortRef.current === controller) {
        setManagementCounts(response.data);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.name === "AbortError") return;
      console.error("Error fetching management counts:", error);
    }
  }, [getAccessToken]);

  // Fetch app families - accepts page as parameter
  const fetchFamilies = useCallback(async (pageNum = page) => {
    if (familiesAbortRef.current) familiesAbortRef.current.abort();
    const controller = new AbortController();
    familiesAbortRef.current = controller;
    try {
      setLoading(true);
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = { 
        search: debouncedSearch, 
        page: pageNum, 
        page_size: PAGE_SIZE,
        sort_by: sortConfig.key,
        sort_dir: sortConfig.direction
      };
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      if (managementFilter && managementFilter !== "all") {
        params.management_filter = managementFilter;
      }
      
      const response = await axios.get(`${API}/discover/app-families`, {
        params,
        headers,
        signal: controller.signal,
      });
      
      if (familiesAbortRef.current === controller) {
        setFamilies(response.data.families || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.name === "AbortError") return;
      console.error("Error fetching families:", error);
      toast.error("Failed to fetch app families");
    } finally {
      if (familiesAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, selectedCategory, sortConfig, managementFilter, getAccessToken, page]);
  
  // Fetch filtered app families - accepts page as parameter to avoid stale closure issues
  const fetchFilteredFamilies = useCallback(async (pageNum = page) => {
    if (filteredFamiliesAbortRef.current) filteredFamiliesAbortRef.current.abort();
    const controller = new AbortController();
    filteredFamiliesAbortRef.current = controller;
    try {
      setLoading(true);
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/app-families-filtered`, {
        params: { 
          search: debouncedSearch, 
          page: pageNum, 
          page_size: PAGE_SIZE,
          exclude_system: filters.excludeSystem,
          exclude_drivers: filters.excludeDrivers,
          exclude_non_business: filters.excludeNonBusiness,
          exclude_redistributable: filters.excludeRedistributable,
          exclude_help_files: filters.excludeHelpFiles,
          exclude_deprecated: filters.excludeDeprecated,
          exclude_installation_files: filters.excludeInstallationFiles,
          exclude_bundle: filters.excludeBundle,
        },
        headers,
        signal: controller.signal,
      });
      
      if (filteredFamiliesAbortRef.current === controller) {
        setFamilies(response.data.families || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.name === "AbortError") return;
      console.error("Error fetching filtered families:", error);
      toast.error("Failed to fetch filtered app families");
    } finally {
      if (filteredFamiliesAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, filters, getAccessToken, page]);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    const token = await getAccessToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // One-shot retry: at large tenant scale the underlying Cosmos
    // aggregations occasionally need their full retry budget and the
    // first HTTP attempt can still surface as a transient 5xx /
    // timeout. A single 1.5s-delayed retry recovers without making the
    // user click anything, and preserves the previous `stats` value on
    // hard failure so the tiles don't blank out to "0".
    const attempt = async () => axios.get(`${API}/discover/stats`, { headers, timeout: 60000 });

    try {
      const response = await attempt();
      setStats(response.data);
      setStatsError(false);
    } catch (firstError) {
      console.warn("discover/stats first attempt failed, retrying once:", firstError?.message || firstError);
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const response = await attempt();
        setStats(response.data);
        setStatsError(false);
      } catch (error) {
        console.error("Error fetching stats (after retry):", error);
        // Keep any previously loaded `stats` in state so the dashboard
        // doesn't flash to zeros on a transient failure. Only flip
        // statsError when we have nothing at all to show.
        setStatsError((prev) => (stats ? prev : true));
      }
    }
  }, [getAccessToken, stats]);
  
  // Fetch rationalisation summary
  const fetchRationalisation = useCallback(async () => {
    // Cancel any in-flight call so its (possibly stale) response cannot
    // overwrite a newer one.
    if (rationalisationAbortRef.current) {
      rationalisationAbortRef.current.abort();
    }
    const controller = new AbortController();
    rationalisationAbortRef.current = controller;

    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const endpoint = filtersEnabled 
        ? `${API}/discover/rationalization-summary-filtered`
        : `${API}/discover/rationalization-summary`;
      
      const params = filtersEnabled ? {
        exclude_system: filters.excludeSystem,
        exclude_drivers: filters.excludeDrivers,
        exclude_non_business: filters.excludeNonBusiness,
        exclude_redistributable: filters.excludeRedistributable,
        exclude_help_files: filters.excludeHelpFiles,
        exclude_deprecated: filters.excludeDeprecated,
        exclude_installation_files: filters.excludeInstallationFiles,
        exclude_bundle: filters.excludeBundle,
      } : {};
      
      const response = await axios.get(endpoint, { params, headers, signal: controller.signal });
      // Belt-and-braces: only commit state if this controller is still
      // the active one (not superseded by a newer request).
      if (rationalisationAbortRef.current === controller) {
        setRationalisation(response.data);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.name === "AbortError") {
        return; // expected; a newer request supersedes this one
      }
      console.error("Error fetching rationalisation:", error);
    }
  }, [filtersEnabled, filters, getAccessToken]);
  
  // Fetch AppsDatabase stats
  const fetchAppsDbStats = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/apps-database/stats`, { headers });
      setAppsDbStats(response.data);
      
      // Enable filters if AppsDatabase is imported
      if (response.data.imported) {
        setFiltersEnabled(true);
      }
    } catch (error) {
      console.error("Error fetching AppsDatabase stats:", error);
    }
  }, [getAccessToken]);
  
  // Fetch CVE database stats
  const fetchCveDbStats = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/cve-database/stats`, { headers });
      setCveDbStats(response.data);
    } catch (error) {
      console.error("Error fetching CVE database stats:", error);
    }
  }, [getAccessToken]);
  
  // Fetch Winget database stats
  const fetchWingetDbStats = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/winget-database/stats`, { headers });
      setWingetDbStats(response.data);
    } catch (error) {
      console.error("Error fetching Winget database stats:", error);
    }
  }, [getAccessToken]);
  
  // Fetch vulnerable apps - accepts page as parameter
  const fetchVulnerableApps = useCallback(async (pageNum = page) => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/vulnerable-apps`, {
        params: { search: debouncedSearch, page: pageNum, page_size: PAGE_SIZE },
        headers
      });
      
      setVulnerableApps(response.data.apps || []);
      setVulnStats(response.data.stats || null);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Error fetching vulnerable apps:", error);
      setVulnerableApps([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, getAccessToken, page]);
  
  // Fetch upgradable apps - accepts page as parameter
  const fetchUpgradableApps = useCallback(async (pageNum = page) => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/upgradable-apps`, {
        params: { search: debouncedSearch, page: pageNum, page_size: PAGE_SIZE },
        headers
      });
      
      setUpgradableApps(response.data.apps || []);
      setUpgradeStats(response.data.stats || null);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Error fetching upgradable apps:", error);
      setUpgradableApps([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, getAccessToken, page]);
  
  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/discover/sync-status`, { headers });
      setSyncStatus(response.data);
      
      if (response.data.status === "in_progress") {
        setSyncing(true);
      } else {
        setSyncing(false);
      }
      
      return response.data;
    } catch (error) {
      console.error("Error fetching sync status:", error);
      return null;
    }
  }, [getAccessToken]);
  
  // Start sync
  const startSync = async () => {
    try {
      setSyncing(true);
      const token = await getAccessToken();
      
      // Note: X-ALICE-Tenant-ID header is added automatically by axios interceptor in App.js
      await axios.post(`${API}/discover/sync`, {}, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Sync started");
      
      pollSyncStatus();
    } catch (error) {
      console.error("Error starting sync:", error);
      const message = error.response?.data?.detail || "Failed to start sync";
      toast.error(message);
      setSyncing(false);
    }
  };

  /**
   * Re-run rationalisation against the current AppsDatabase.
   *
   * The AppsDatabase is central, so when it is updated a tenant's own
   * flags are still whatever the last matching run wrote. An Intune
   * tenant picks the change up on its overnight sync; an SCCM tenant has
   * no overnight sync, so this is how they get the better-filtered view
   * without re-uploading their whole estate.
   *
   * Returns 202 and reports on the same sync-status document as a sync,
   * so the existing progress overlay follows it with no extra plumbing.
   */
  const startReRationalise = async () => {
    try {
      setSyncing(true);
      const token = await getAccessToken();

      const response = await axios.post(`${API}/discover/re-rationalise`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(
        `Re-rationalising ${(response.data.rationalising ?? 0).toLocaleString()} applications`
      );

      pollSyncStatus();
    } catch (error) {
      console.error("Error starting re-rationalisation:", error);
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Failed to start re-rationalisation"
      );
      setSyncing(false);
    }
  };

  // Sync packaging status from deployed packages
  // We read the post-sync refresh fetchers off refs so the call uses the
  // *latest* closures — not the ones captured when this component first
  // rendered (when filtersEnabled was still false). Without this, the
  // post-sync `fetchRationalisation()` call below would hit the unfiltered
  // endpoint and overwrite the Rationalised tile with the wrong count.
  const fetchRationalisationLatest = useRef(fetchRationalisation);
  const fetchFilteredFamiliesLatest = useRef(fetchFilteredFamilies);
  useEffect(() => { fetchRationalisationLatest.current = fetchRationalisation; });
  useEffect(() => { fetchFilteredFamiliesLatest.current = fetchFilteredFamilies; });

  const syncPackagingStatus = async (silent = false) => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (!silent) toast.info("Syncing deployment status...");

      // The silent on-mount call lets the server skip the estate-wide
      // rewrite when packages/deployments are unchanged; the manual
      // button always forces a full run.
      const response = await axios.post(`${API}/discover/sync-packaging-status`, {}, {
        headers, params: { force: !silent },
      });
      const data = response.data;

      if (data.skipped) {
        if (!silent) toast.info("Deployment status already up to date");
      } else if (data.matched_apps > 0) {
        if (!silent) toast.success(`Found ${data.matched_apps} deployed app(s) from ${data.total_packages_checked} package(s)`);
        // Wait for both refreshes so the caller can rely on the UI
        // being up-to-date when this promise resolves. Use the *latest*
        // fetchers (via refs) so we don't fire the unfiltered endpoint
        // with a stale-closure filtersEnabled=false.
        await Promise.all([fetchFilteredFamiliesLatest.current(), fetchRationalisationLatest.current()]);
      } else {
        if (!silent) toast.info("No deployed packages found to sync");
      }
      return data;
    } catch (error) {
      console.error("Error syncing packaging status:", error);
      if (!silent) toast.error("Failed to sync deployment status");
      return null;
    }
  };
  
  // Toggle out-of-scope for a family
  const toggleOutOfScope = async (familyName, e) => {
    if (e) e.stopPropagation();
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/discover/toggle-out-of-scope`,
        { family_name: familyName },
        { headers }
      );
      const { out_of_scope } = response.data;
      toast.success(`${familyName} marked as ${out_of_scope ? 'Out of Scope' : 'In Scope'}`);
      // Refresh data
      fetchFamilies();
      fetchManagementCounts();
    } catch (error) {
      console.error("Error toggling out of scope:", error?.response?.data || error);
      const detail = error?.response?.data?.detail || "Failed to update scope status";
      toast.error(detail);
    }
  };
  
  // Poll sync status
  const pollSyncStatus = useCallback(async () => {
    const status = await fetchSyncStatus();
    
    if (status?.status === "in_progress") {
      setTimeout(pollSyncStatus, 2000);
    } else if (status?.status === "completed") {
      if (status.source === "re_rationalisation") {
        // `total_apps` is still the last import's count -- nothing was
        // discovered here. The backend's summary line is what changed.
        toast.success(status.current_step || "Re-rationalisation complete");
        const failed = Object.entries(status.enrichment || {})
          .filter(([, outcome]) => outcome === "failed")
          .map(([name]) => name);
        if (failed.length) {
          toast.warning(
            `Finished, but ${failed.join(" and ")} failed -- those columns may be stale.`,
            { duration: 10000 }
          );
        }
      } else {
        toast.success(`Sync completed: ${status.total_apps} apps discovered`);
      }
      fetchApps();
      fetchFamilies();
      fetchStats();
      fetchRationalisation();
    } else if (status?.status === "failed") {
      const errorMsg = status.error || "";
      if (errorMsg.includes("403") || errorMsg.includes("Forbidden")) {
        toast.error(
          "Permission denied. Please add 'DeviceManagementManagedDevices.Read.All' permission. See Settings page.",
          { duration: 10000 }
        );
      } else {
        toast.error(`Sync failed: ${status.error}`);
      }
    }
  }, [fetchSyncStatus, fetchApps, fetchFamilies, fetchStats, fetchRationalisation]);
  
  // Update URL when viewMode changes (for back navigation support)
  useEffect(() => {
    const currentView = searchParams.get("view");
    if (viewMode !== currentView) {
      setSearchParams({ view: viewMode }, { replace: true });
    }
  }, [viewMode, searchParams, setSearchParams]);
  
  // Export to CSV
  const exportToCSV = async () => {
    if (viewMode === "apps") {
      // For the All Apps view we hit a *streaming* backend endpoint that
      // writes the CSV row-by-row from a single Cosmos cursor. This
      // avoids:
      //   (a) the O(N) cost of paginated skip() on large tenants
      //       (8k apps × skip-and-discard quickly burns ingress timeouts),
      //   (b) tens-of-MB JSON payloads per page when `device_names` is
      //       huge (tenants with thousands of devices), and
      //   (c) any single request holding the entire result set in
      //       memory client- or server-side.
      // We use fetch() (not axios) so we can stream the response body to
      // a Blob without buffering it through JSON.parse, and attach the
      // same auth + tenant headers the axios interceptor would.
      setExporting(true);
      try {
        const token = await getAccessToken();
        const selectedTenantId = localStorage.getItem("alice_selected_tenant_id");

        const url = new URL(`${API}/discover/detected-apps/export`);
        if (debouncedSearch) url.searchParams.set("search", debouncedSearch);

        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (selectedTenantId) headers["X-ALICE-Tenant-ID"] = selectedTenantId;

        const resp = await fetch(url.toString(), {
          method: "GET",
          headers,
          credentials: "include",
        });

        // Diagnostic headers from the streaming endpoint — invaluable
        // for confirming which build is actually live and how many rows
        // the backend *intended* to send (vs. how many landed in the
        // saved file).
        const exportVersion = resp.headers.get("X-Alice-Export-Version");
        const expectedRows = resp.headers.get("X-Alice-Export-Expected-Rows");
        const targetTenant = resp.headers.get("X-Alice-Export-Target-Tenant");
        console.log("[Export CSV] response status:", resp.status, {
          exportVersion,
          expectedRows,
          targetTenant,
          contentType: resp.headers.get("Content-Type"),
          contentLength: resp.headers.get("Content-Length"),
        });

        if (!resp.ok) {
          // Try to read the body for a useful error message.
          let bodySnippet = "";
          try { bodySnippet = (await resp.text()).slice(0, 200); } catch (_) { /* ignore */ }
          throw new Error(`Export failed: HTTP ${resp.status}${bodySnippet ? ` — ${bodySnippet}` : ""}`);
        }

        if (!exportVersion) {
          // We hit a route that's NOT our streaming endpoint (e.g. an
          // older build or a path collision returning JSON). Bail loudly
          // so the user knows the deployment is stale instead of
          // silently saving a 50-row file.
          let bodySnippet = "";
          try { bodySnippet = (await resp.text()).slice(0, 300); } catch (_) { /* ignore */ }
          throw new Error(
            `Export endpoint missing diagnostic headers — this build does not include the streaming export fix. ` +
            `Hard-refresh the page (Ctrl+Shift+R) and retry. Body sample: ${bodySnippet}`
          );
        }

        const blob = await resp.blob();
        if (blob.size === 0) {
          toast.error("No data to export");
          return;
        }

        // Count rows in the blob so we can compare to expectedRows.
        // (Rough — counts newlines minus the header. Good enough for
        // a sanity check; exact count is in the trailer line.)
        let actualRows = -1;
        try {
          const text = await blob.text();
          actualRows = Math.max(0, text.split("\n").filter(l => l && !l.startsWith("#")).length - 1);
        } catch (_) { /* ignore */ }

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `discovered_apps_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        const sizeMb = (blob.size / 1024 / 1024).toFixed(2);
        if (expectedRows && actualRows >= 0 && Number(expectedRows) !== actualRows) {
          toast.warning(
            `Export saved ${actualRows} rows but server reported ${expectedRows} matched. ` +
            `Check the last line of the CSV for a "# ALICE EXPORT TRUNCATED" marker.`
          );
        } else if (expectedRows) {
          toast.success(`Export complete: ${actualRows >= 0 ? actualRows : expectedRows} rows, ${sizeMb} MB`);
        } else {
          toast.success(`Export complete (${sizeMb} MB)`);
        }
      } catch (error) {
        console.error("Error exporting apps to CSV:", error);
        toast.error(`Failed to export apps: ${error.message || error}`);
      } finally {
        setExporting(false);
      }
      return;
    }

    // Families view — uses the in-memory page (unchanged behaviour).
    const dataToExport = families;
    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }

    // CSV header for families
    let csvContent = "Family Name,Sample Display Name,Publisher,Version Count,Highest Version,Most Installed Version,Total Device Count\n";

    dataToExport.forEach(family => {
      csvContent += `"${(family.family_name || "").replace(/"/g, '""')}","${(family.sample_display_name || "").replace(/"/g, '""')}","${(family.publisher || "").replace(/"/g, '""')}",${family.version_count || 0},"${family.highest_version || ""}","${family.most_installed_version || ""}",${family.total_device_count || 0}\n`;
    });

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `discovered_${viewMode}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();

    // Cleanup after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);

    toast.success(`Exported ${dataToExport.length} ${viewMode} to CSV`);
  };

  // Update risk level for an app family
  const updateRiskLevel = async (familyName, riskLevel) => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(
        `${API}/discover/governance/${encodeURIComponent(familyName)}`,
        { risk_level: riskLevel },
        { headers }
      );
      
      // Update local state to reflect the change
      setFamilies(prev => prev.map(f => 
        f.family_name === familyName ? { ...f, risk_level: riskLevel } : f
      ));
      
      toast.success(`Business criticality updated to ${riskLevel === 'green' ? 'Low' : riskLevel === 'amber' ? 'Medium' : 'High'}`);
    } catch (error) {
      console.error("Error updating business criticality:", error);
      toast.error("Failed to update business criticality");
    }
  };

  // Export Rationalised Apps to CSV - fetches ALL data from backend
  const [exporting, setExporting] = useState(false);
  
  const exportRationalisedToCSV = async () => {
    setExporting(true);
    try {
      const token = await getAccessToken();
      
      // Fetch ALL families from the export endpoint (no pagination)
      const params = {};
      if (search) params.search = search;
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await axios.get(`${API}/discover/app-families/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      const allFamilies = response.data.families || [];
      
      if (allFamilies.length === 0) {
        toast.error("No data to export");
        return;
      }
      
      let csvContent = "Family Name,Publisher,Version Count,Device Count,Highest Version,Category,Winget Matched,Winget Package ID,System Component,Bundle,Help Files,Deprecated\n";
      
      allFamilies.forEach(family => {
        const rat = family.rationalization || family.rationalisation || {};
        csvContent += `"${(family.family_name || "").replace(/"/g, '""')}",`;
        csvContent += `"${(family.publisher || "").replace(/"/g, '""')}",`;
        csvContent += `${family.version_count || 0},`;
        csvContent += `${family.unique_device_count || family.total_device_count || 0},`;
        csvContent += `"${family.highest_version || ""}",`;
        csvContent += `"${(family.app_category || rat.category || "").replace(/"/g, '""')}",`;
        csvContent += `${family.winget_matched || false},`;
        csvContent += `"${family.winget_package_id || ""}",`;
        csvContent += `${rat.system_component || false},`;
        csvContent += `${rat.bundle || false},`;
        csvContent += `${rat.help_files || false},`;
        csvContent += `${rat.deprecated || false}\n`;
      });
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rationalised_apps_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Exported ${allFamilies.length} rationalised apps to CSV`);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };
  
  // Initial load — guarded so it fires exactly once per page load, even
  // if fetcher useCallback identities change (e.g. when fetchAppsDbStats
  // flips filtersEnabled and the cascade re-creates fetchRationalisation).
  // Without the guard the entire effect (including the heavy
  // syncPackagingStatus POST) re-ran on every callback-identity churn,
  // doubling Cosmos RU pressure on every Discover page mount.
  const bootRanRef = useRef(false);
  useEffect(() => {
    if (bootRanRef.current) return;
    bootRanRef.current = true;

    fetchStats();
    fetchSyncStatus();
    fetchRationalisation();
    fetchAppsDbStats();
    fetchCveDbStats();
    fetchWingetDbStats();
    fetchCategories();
    fetchManagementCounts();
    // Auto-sync deployment status silently on page load and wait until it
    // completes (+ a short 600ms settle) before unblocking the table.
    // Prevents users from clicking a category and seeing stale rows while
    // the sync is mid-flight.
    let cancelled = false;
    (async () => {
      try {
        await syncPackagingStatus(true);
      } catch (e) {
        // syncPackagingStatus already handles errors; ignore here.
      }
      if (cancelled) return;
      setTimeout(() => {
        if (!cancelled) setInitialSyncing(false);
      }, 600);
    })();
    return () => { cancelled = true; };
    // Boot-only: deliberately empty deps. The bootRanRef guard above
    // prevents a re-run if any caller mounts us under React 18 strict
    // mode or our useCallback identities churn (which they will, since
    // fetchRationalisation depends on filtersEnabled). Legitimate
    // refetch-on-filter-change is handled by the [viewMode, filtersEnabled]
    // effect below.
  }, []);

  // Defensive: explicitly drive the sync when we arrived via "Back to
  // Discover" from the Apps Governance page. The boot effect above will
  // already have kicked syncPackagingStatus(true) on this fresh mount,
  // so under today's React Router behavior this effect is a no-op safety
  // net. It exists so future refactors (e.g. nested routes / <Outlet/>)
  // that keep DiscoverPage mounted across the governance detour cannot
  // silently break the "table is checked/synced prior to displaying"
  // guarantee the user relies on.
  //
  // We coordinate with boot by checking initialSyncing: if boot already
  // flipped it on (it starts as `true` and only gets flipped off after
  // sync completes), we don't need to fire a second sync. Boot always
  // owns the sync on a fresh mount; this only kicks in if boot didn't.
  const backFromGovernanceHandledRef = useRef(false);
  useEffect(() => {
    if (!backFromGovernanceSyncRef.current) return;
    if (backFromGovernanceHandledRef.current) return;
    backFromGovernanceHandledRef.current = true;

    // Boot's sync is already in flight on a normal remount — initialSyncing
    // will be true and we can trust the block-until-sync UX. Bail out.
    if (initialSyncing) return;

    // Boot didn't run its sync for whatever reason. Reproduce the same
    // gated silent-sync UX the user gets on initial page load.
    setInitialSyncing(true);
    let cancelled = false;
    (async () => {
      try {
        await syncPackagingStatus(true);
      } catch (e) {
        // syncPackagingStatus handles/logs errors internally.
      }
      if (cancelled) return;
      setTimeout(() => {
        if (!cancelled) setInitialSyncing(false);
      }, 600);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load data when view mode changes (NOT when fetch functions change)
  useEffect(() => {
    if (viewMode === "apps") {
      fetchApps();
    } else if (viewMode === "rationalisation") {
      fetchRationalisation();
      fetchFilteredFamilies();
    } else if (viewMode === "vulnerabilities") {
      fetchVulnerableApps();
    } else if (viewMode === "upgrades") {
      fetchUpgradableApps();
    }
  }, [viewMode, filtersEnabled]);
  
  // Refetch when filters change
  useEffect(() => {
    if (filtersEnabled && (viewMode === "families" || viewMode === "rationalisation")) {
      if (viewMode === "families") {
        fetchFilteredFamilies();
      } else {
        fetchRationalisation();
      }
    }
  }, [filters]);
  
  // Refetch families when category changes
  useEffect(() => {
    if (viewMode === "rationalisation") {
      setPage(1);
      fetchFamilies(1);
    }
  }, [selectedCategory]);
  
  // Refetch families when management filter changes
  useEffect(() => {
    if (viewMode === "rationalisation") {
      setPage(1);
      fetchFamilies(1);
    }
  }, [managementFilter]);
  
  // Refetch families when sort changes
  useEffect(() => {
    if (viewMode === "rationalisation") {
      setPage(1); // Reset to first page when sort changes
      fetchFamilies(1); // Pass page 1 explicitly
    }
  }, [sortConfig]);
  
  // Poll if syncing on mount
  useEffect(() => {
    if (syncStatus?.status === "in_progress") {
      pollSyncStatus();
    }
  }, [syncStatus?.status, pollSyncStatus]);
  
  // Refetch when debounced search changes (pagination is handled directly by button clicks)
  useEffect(() => {
    // Reset to page 1 when search changes
    setPage(1);
    if (viewMode === "apps") {
      fetchApps(1);
    } else if (viewMode === "rationalisation") {
      fetchFilteredFamilies(1);
    } else if (viewMode === "vulnerabilities") {
      fetchVulnerableApps(1);
    } else if (viewMode === "upgrades") {
      fetchUpgradableApps(1);
    }
  }, [debouncedSearch]);
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    if (viewMode === "apps") {
      fetchApps();
    } else if (viewMode === "families") {
      if (filtersEnabled) {
        fetchFilteredFamilies();
      } else {
        fetchFamilies();
      }
    } else if (viewMode === "vulnerabilities") {
      fetchVulnerableApps();
    } else if (viewMode === "upgrades") {
      fetchUpgradableApps();
    }
  };
  
  // Get severity badge style
  const getSeverityBadge = (severity) => {
    if (!severity) return null;
    const styles = {
      CRITICAL: "bg-red-600 text-white",
      HIGH: "bg-orange-500 text-white",
      MEDIUM: "bg-yellow-500 text-black",
      LOW: "bg-blue-500 text-white"
    };
    return (
      <Badge className={`${styles[severity] || "bg-gray-500 text-white"} text-xs`}>
        {severity}
      </Badge>
    );
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Render a stats tile value. Returns "—" while we don't yet have a
  // populated `stats` object, or when the backend reported this specific
  // field as failed in `failed_fields` (e.g. a Cosmos throttle exhausted
  // its retry budget on one sub-aggregation). Falls back to the value
  // formatter otherwise. This stops the tiles from flashing "0" /
  // "Never" on large-tenant Cosmos throttle, which the user (correctly)
  // reads as "the app is broken".
  const statValue = (field, value, fmt) => {
    if (!stats || statsError) return "—";
    const failed = Array.isArray(stats.failed_fields) && stats.failed_fields.includes(field);
    if (failed) return "—";
    if (value === null || value === undefined) return "—";
    return fmt ? fmt(value) : value;
  };

  // Tile badge for the change at the last sync or import, as frozen when it
  // finished. null means there was nothing comparable (first sync, or the
  // figure's definition changed), so no badge rather than a misleading one.
  const summaryVarianceBadge = (field) => {
    const value = stats?.summary_variance?.[field];
    if (value === null || value === undefined || value === 0) return null;
    const at = stats?.summary_variance_at ? new Date(stats.summary_variance_at).toLocaleString() : null;
    return (
      <span
        className={`text-sm font-semibold px-2 py-0.5 rounded ${
          value > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}
        title={at ? `Change at the last sync (${at})` : "Change at the last sync"}
      >
        {value > 0 ? '↑' : '↓'}{Math.abs(value).toLocaleString()}
      </span>
    );
  };

  // The Rationalised tile's badge diffs its count with every filter on (the
  // default). With filters off or changed, the tile counts something else,
  // so the badge would describe a different number.
  const allRationalisationFiltersOn = filtersEnabled && Object.values(filters).every(Boolean);

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return "-";
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(1)} KB`;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };
  
  // Open device details modal
  const openDeviceDetails = (app) => {
    setSelectedApp(app);
    setShowDevicesModal(true);
  };

  // Open CVE details modal
  const openCveDetails = (app, e) => {
    if (e) {
      e.stopPropagation(); // Prevent row click from triggering
    }
    if (app?.cve_data?.matched && app?.cve_data?.cves?.length > 0) {
      setSelectedCveApp(app);
      setShowCveModal(true);
    }
  };

  // Get severity badge color classes
  const getSeverityColor = (severity) => {
    const colors = {
      CRITICAL: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-500' },
      HIGH: { bg: 'bg-orange-500', text: 'text-white', border: 'border-orange-400' },
      MEDIUM: { bg: 'bg-yellow-500', text: 'text-black', border: 'border-yellow-400' },
      LOW: { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-400' }
    };
    return colors[severity] || { bg: 'bg-gray-500', text: 'text-white', border: 'border-gray-400' };
  };

  // Handle package app action
  const handlePackageApp = async (item) => {
    const appName = item.family_name || item.display_name_normalized || item.display_name;
    const wingetId = item.winget_data?.package_identifier || item.winget_package_id;
    
    if (!wingetId) {
      toast.error(`No Winget package ID found for ${appName}. Use Custom Upload instead.`);
      return;
    }
    
    // Navigate to catalog with wingetId to auto-open modal
    // Status will be updated when user actually completes packaging
    navigate("/catalog", { 
      state: { 
        autoPackage: true,
        wingetId: wingetId,
        fromDiscover: true,
        discoverFamilyName: appName  // Original family name for linking back after packaging
      }
    });
  };

  // Handle custom upload action
  const handleCustomUpload = (item) => {
    const appName = item.family_name || item.display_name || "app";
    const familyName = item.family_name || item.display_name_normalized || appName;
    const publisher = (item.publishers && item.publishers[0]) || item.publisher || "";
    
    navigate("/catalog", { 
      state: { 
        customUpload: true,
        appName: appName,
        familyName: familyName,
        publisher: publisher,
        fromDiscover: true
      }
    });
  };

  // Handle "Package from Private Repository" action.
  // Replaces Custom Upload when the family matches a folder in the
  // tenant's Private Repository (see PRIVATE_REPO_DESIGN.md §6.2).
  // The catalog modal opens in "Private Repo mode": no file picker,
  // the latest blob is fetched server-side and analysed.
  const handlePackageFromPrivateRepo = (item) => {
    const appName = item.family_name || item.display_name || "app";
    const familyName = item.family_name || item.display_name_normalized || appName;
    const publisher = (item.publishers && item.publishers[0]) || item.publisher || "";
    const latestVersion = item.private_repo_latest_version;

    navigate("/catalog", {
      state: {
        customUpload: true,
        privateRepo: true,
        privateRepoLatestVersion: latestVersion,
        appName: appName,
        familyName: familyName,
        publisher: publisher,
        fromDiscover: true,
      },
    });
  };

  // "Uninstall only" confirmation. Building the package is cheap and
  // reversible (nothing is assigned in Intune until the package is deployed
  // to Test), but the action reads as destructive, so it is confirmed with
  // the family name and its device count in front of the user.
  const handleUninstallOnly = (item) => {
    setUninstallOnlyTarget(item);
  };

  const confirmUninstallOnly = async () => {
    const item = uninstallOnlyTarget;
    if (!item || uninstallOnlyBusy) return;

    const appName = item.family_name || item.display_name_normalized || item.display_name;
    const publisher = (item.publishers && item.publishers[0]) || item.publisher || "";

    setUninstallOnlyBusy(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/packages/uninstall-only`,
        {
          app_name: appName,
          publisher: publisher,
          // Highest observed version is metadata only — the package detects
          // the app by name at any version so every install gets removed.
          version: item.highest_version || null,
          discover_family_name: item.family_name || appName,
        },
        { headers }
      );

      toast.success(
        `Uninstall-only package created for ${appName}. Deploy it to Test from the Packages page.`
      );
      setUninstallOnlyTarget(null);

      // Reflect the new "Uninstall - Packaged" state without a full reload.
      if (viewMode === "rationalisation") {
        fetchRationalisation();
      }
      fetchFamilies();

      return response.data;
    } catch (error) {
      console.error("Error creating uninstall-only package:", error);
      toast.error(
        error.response?.data?.detail ||
          `Failed to create uninstall-only package for ${appName}`
      );
    } finally {
      setUninstallOnlyBusy(false);
    }
  };

  // Fetch test approvals for a package
  const fetchTestApprovals = async (packageId) => {
    try {
      const response = await axios.get(`${API}/packages/${packageId}/test-approvals`);
      return response.data;
    } catch (error) {
      console.error("Error fetching test approvals:", error);
      return null;
    }
  };

  // Fetch governance to check for configured testers
  const fetchGovernanceTesters = async (familyName) => {
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API}/discover/governance/${encodeURIComponent(familyName)}`,
        { headers }
      );
      return response.data?.governance?.testers || [];
    } catch (error) {
      console.error("Error fetching governance:", error);
      return [];
    }
  };

  // Fetch the stage names for families mid-rollout, once per family and
  // package, so the table can say "LIVE - Pilot" and "Go to Friends and
  // Family" rather than assuming three numbered phases. Best effort: on a
  // failure the row falls back to "Phase N" / "Next stage".
  useEffect(() => {
    const pending = families.filter((f) => {
      const status = f.packaging_status?.status;
      if (!status?.startsWith("live_phase")) return false;
      return !requestedStageKeys.current.has(familyStageKey(f));
    });
    if (!pending.length) return;
    pending.forEach((f) => requestedStageKeys.current.add(familyStageKey(f)));
    pending.forEach(async (f) => {
      const key = familyStageKey(f);
      const name = f.family_name || f.display_name_normalized || f.display_name;
      try {
        const res = await axios.get(`${API}/discover/go-live-by-app/${encodeURIComponent(name)}`);
        const dep = res.data?.found ? res.data.deployment : null;
        if (!dep) return;
        const phasesObj = dep.phases || {};
        const keyed = Object.keys(phasesObj)
          .map((k) => /^phase(\d+)$/.exec(k))
          .filter(Boolean)
          .map((m) => Number(m[1]));
        const count = Number(dep.phase_count) > 0
          ? Number(dep.phase_count)
          : (keyed.length ? Math.max(...keyed) : null);
        const names = count
          ? Array.from({ length: count }, (_, i) => stageName(phasesObj[`phase${i + 1}`], i + 1))
          : [];
        setLiveStageInfo((prev) => ({ ...prev, [key]: { names, count } }));
      } catch (error) {
        console.error("Error fetching rollout stages:", error);
      }
    });
  }, [families]);

  // Handle Go Live phased deployment
  const handleGoLive = async (item, forceOverride = false) => {
    const appName = item.family_name || item.display_name_normalized || item.display_name;
    const displayNameNormalized = item.family_name || item.display_name_normalized;
    const packageId = item.packaging_status?.package_id;
    const status = item.packaging_status?.status;
    
    // If already in a live phase, proceed to next phase (not start over)
    if (status?.startsWith("live_phase") || status === "live") {
      await handleProceedToNextPhase(item);
      return;
    }
    
    // If all_testers_approved flag is already set, proceed directly to Phase 1
    if (item.packaging_status?.all_testers_approved) {
      await executeGoLive(item);
      return;
    }
    
    // Check test approvals if we have a package ID and not forcing override
    if (packageId && !forceOverride) {
      const approvals = await fetchTestApprovals(packageId);
      const configuredTesters = await fetchGovernanceTesters(displayNameNormalized);
      
      if (approvals && approvals.summary) {
        const { total, approved, rejected, pending } = approvals.summary;
        
        // If there are approval records, check them
        if (total > 0) {
          // Store approvals for display
          setTestApprovals(prev => ({ ...prev, [packageId]: approvals }));
          
          // Check if any rejections
          if (rejected > 0) {
            toast.error(`Cannot Go Live: ${rejected} tester(s) rejected the application`);
            return;
          }
          
          // Check if not all approved yet
          if (pending > 0) {
            setPendingGoLiveItem(item);
            setShowGoLiveWarning(true);
            return;
          }
        } 
        // If no approval records but testers are configured, warn
        else if (configuredTesters.length > 0) {
          // Create a mock approvals object to show in the warning
          setTestApprovals(prev => ({ 
            ...prev, 
            [packageId]: {
              approvals: configuredTesters.map(t => ({ tester_email: t, status: 'not_notified' })),
              summary: { total: configuredTesters.length, approved: 0, rejected: 0, pending: configuredTesters.length }
            }
          }));
          setPendingGoLiveItem(item);
          setShowGoLiveWarning(true);
          return;
        }
      }
    }
    
    // Proceed with Go Live (Phase 1)
    await executeGoLive(item);
  };

  // Execute the actual Go Live deployment
  const executeGoLive = async (item) => {
    const appName = item.family_name || item.display_name_normalized || item.display_name;
    const displayNameNormalized = item.family_name || item.display_name_normalized;
    
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      toast.info(`Initiating Go LIVE for ${appName}...`);
      
      // The backend will look up the intune_app_id from packages collection
      const response = await axios.post(`${API}/discover/go-live-phased`, {
        display_name_normalized: displayNameNormalized,
        app_name: appName,
        version: item.highest_version || "1.0"
      }, { headers });
      
      const data = response.data;
      // The first stage after UAT may be a percentage sample or a named
      // Entra group (deployment pipelines); word the toast from the response.
      const first = data.phases?.phase1 || {};
      const second = data.phases?.phase2;
      const devicesText = first.device_count != null
        ? ` ${first.device_count}${data.total_devices != null ? ` of ${data.total_devices}` : ""} devices.`
        : isNamedGroupStage(first) && first.group_name
          ? ` Assigning to Entra group '${first.group_name}'.`
          : "";
      const nextText = second ? ` Monitor success rate, then proceed to ${stageName(second, 2)}.` : "";
      toast.success(`${stageLabel(first, 1)} deployed!${devicesText}${nextText}`, { duration: 10000 });
      
      // Store deployment ID for phase progression
      localStorage.setItem(`go_live_${displayNameNormalized}`, data.deployment_id);
      
      // Sync packaging status so the Rationalised Applications table
      // immediately reflects the new LIVE / Phase 1 state. Without this the
      // detected_apps.packaging_status stays on its previous value
      // (deployed_test/approved) and the row keeps showing the "Go Live"
      // button until a later background sync happens to run.
      await syncPackagingStatus(true);
      
      // Refresh the data
      fetchFilteredFamilies();
    } catch (error) {
      console.error("Error initiating Go Live:", error);
      const errorMsg = error.response?.data?.detail || "Failed to initiate Go Live";
      toast.error(errorMsg);
    }
  };

  // Handle Go Live override (admin bypassing pending approvals)
  const handleGoLiveOverride = () => {
    setShowGoLiveWarning(false);
    if (pendingGoLiveItem) {
      executeGoLive(pendingGoLiveItem);
      setPendingGoLiveItem(null);
    }
  };

  // Proceed to next phase
  const handleProceedToNextPhase = async (item) => {
    const displayNameNormalized = item.family_name || item.display_name_normalized;
    const appName = item.family_name || item.display_name_normalized || item.display_name;
    
    // ALWAYS resolve the CURRENT deployment from the backend first.
    //
    // localStorage caches the last deployment_id we started for this
    // family, but that becomes STALE the moment an auto-updated version
    // (e.g. "Zoom" -> "Zoom Workplace") gets a fresh Intune app id and a
    // brand-new phased_deployments doc. Trusting the cached id made the
    // Phase 1 -> Phase 2 click target the PREVIOUS version's deployment,
    // which was already at Phase 3, so the backend correctly refused with
    // "Already at Phase 3" (customer report Aug 2026). The backend returns
    // the MOST RECENT deployment for the family, which is the version we
    // are actually rolling out now.
    let deploymentId = null;
    try {
      const response = await axios.get(`${API}/discover/go-live-by-app/${encodeURIComponent(appName)}`);
      if (response.data.found && response.data.deployment) {
        deploymentId = response.data.deployment.deployment_id;
        // Refresh the cache so it always points at the current version.
        localStorage.setItem(`go_live_${displayNameNormalized}`, deploymentId);
      }
    } catch (error) {
      console.error("Error fetching deployment:", error);
    }
    
    // Fall back to the cached id only if the backend returned nothing.
    if (!deploymentId) {
      deploymentId = localStorage.getItem(`go_live_${displayNameNormalized}`);
    }
    
    if (!deploymentId) {
      toast.error("No active deployment found. Please initiate Go Live first.");
      return;
    }
    
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      toast.info("Proceeding to the next stage...");

      const response = await axios.post(`${API}/discover/go-live-proceed/${deploymentId}`, {}, { headers });
      const data = response.data;

      // next_phase_name already carries the percentage ("Phase 2 (50%)");
      // named groups have none and no device count until they run.
      const nextName = data.next_phase_name
        || (data.percentage != null ? `Phase ${data.next_phase} (${data.percentage}%)` : `Phase ${data.next_phase}`);
      toast.success(
        `${nextName} deployed!${data.device_count != null ? ` ${data.device_count} devices.` : ""}`,
        { duration: 8000 }
      );
      
      // Sync packaging status to update UI
      await axios.post(`${API}/discover/sync-packaging-status`);
      
      fetchFilteredFamilies();
    } catch (error) {
      console.error("Error proceeding to next phase:", error);
      const errorMsg = error.response?.data?.detail || "Failed to proceed to next phase";
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Full-Page Sync Overlay - Blocks all interaction during sync */}
      {(syncing || (syncStatus?.status === "in_progress")) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/95 backdrop-blur-sm">
          <div className="w-full max-w-lg mx-4">
            <div className="alice-popup bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl border border-cyan-500/30 shadow-2xl shadow-cyan-500/10 p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="relative inline-block mb-4">
                  <div className="absolute inset-0 rounded-full bg-cyan-400/20 animate-ping" />
                  <div className="relative bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full p-4">
                    <RefreshCw className="h-10 w-10 text-white animate-spin" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {syncStatus?.source === "re_rationalisation"
                    ? "Re-rationalising"
                    : "Syncing in Progress"}
                </h2>
                <p className="text-slate-400 text-sm">
                  {syncStatus?.source === "re_rationalisation"
                    ? "Re-checking your applications against the applications database."
                    : "Please wait while we process your data."}
                </p>
                {/* Estimated Time */}
                {syncStatus?.estimated_time && (
                  <p className="text-cyan-400 text-sm font-medium mt-2">
                    {syncStatus.estimated_time}
                  </p>
                )}
              </div>
              
              {/* Progress Section */}
              <div className="space-y-4">
                {/* Current Phase & Progress */}
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-cyan-400 uppercase tracking-wide">
                      {syncStatus?.phase || "Processing"}
                    </span>
                    <span className="text-lg font-bold text-white">
                      {syncStatus?.progress || 0}%
                    </span>
                  </div>
                  <Progress value={syncStatus?.progress || 0} className="h-2 bg-slate-700" />
                  <p className="text-sm text-white mt-3 font-medium">
                    {syncStatus?.current_step || "Initializing sync..."}
                  </p>
                </div>
                
                {/* Process Steps - using phase field for accurate status */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Nothing is fetched on a re-rationalisation -- it works
                      off the rows already here -- so showing this step
                      ticked would claim a pull that never happened. */}
                  {syncStatus?.source !== "re_rationalisation" && (
                    <StepIndicator
                      label="Fetching from Intune"
                      isActive={syncStatus?.phase === "Fetching from Intune"}
                      isComplete={["Processing & Rationalisation", "Processing & Rationalization", "CVE Matching", "Winget Matching", "Completing", "Complete"].includes(syncStatus?.phase)}
                    />
                  )}
                  <StepIndicator
                    label="Rationalisation" 
                    isActive={syncStatus?.phase === "Processing & Rationalisation" || syncStatus?.phase === "Processing & Rationalization"}
                    isComplete={["CVE Matching", "Winget Matching", "Completing", "Complete"].includes(syncStatus?.phase)}
                  />
                  <StepIndicator 
                    label="CVE Matching" 
                    isActive={syncStatus?.phase === "CVE Matching"}
                    isComplete={["Winget Matching", "Completing", "Complete"].includes(syncStatus?.phase)}
                  />
                  <StepIndicator 
                    label="Winget Matching" 
                    isActive={syncStatus?.phase === "Winget Matching"}
                    isComplete={["Completing", "Complete"].includes(syncStatus?.phase)}
                  />
                </div>
                
                {/* Warning */}
                <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-4">
                  <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  {/* A re-rationalisation runs server-side, so closing the
                      page genuinely does not stop it -- telling the user
                      otherwise just makes them sit and watch a pass that
                      takes minutes on a large estate. */}
                  <p className="text-xs text-amber-200">
                    {syncStatus?.source === "re_rationalisation" ? (
                      <>
                        <strong>This runs on the server.</strong> Discover is locked
                        while every application is re-checked, but the work carries
                        on if you close this page &mdash; come back and it will still
                        be going, or done.
                      </>
                    ) : (
                      <>
                        <strong>Do not close or refresh this page.</strong> The sync
                        process must complete to ensure data integrity. Interrupting
                        may result in incomplete data.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="p-6 max-w-7xl mx-auto">
        {/* Page Title & Sync Button */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Discover</h1>
            <p className="text-white/90 mt-1">
              {canImportSccm && !canSyncIntune
                ? "Applications imported from your SCCM export"
                : "View applications detected across your managed devices"}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {canSyncIntune && (
              <Button
                onClick={startSync}
                disabled={syncing}
                className="flex items-center gap-2 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                data-testid="sync-button"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? "Syncing..." : "Sync from Intune"}
              </Button>
            )}

            {/* SCCM tenants only: an Intune tenant re-rationalises on its
                overnight sync, so the button would be noise there. */}
            {canImportSccm && !canSyncIntune && canManageApps && (
              <Button
                onClick={startReRationalise}
                disabled={syncing}
                variant="outline"
                className="flex items-center gap-2 bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                data-testid="re-rationalise-button"
                title="Re-check every application against the current applications database, without re-importing your estate"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? "Working..." : "Re-rationalise"}
              </Button>
            )}

            {canImportSccm && (
              <Button
                onClick={() => setSccmImportOpen(true)}
                className="flex items-center gap-2 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                data-testid="sccm-import-button"
                title="Import an SCCM SQL export (ARP, RIDARP and Devices CSVs)"
              >
                <Upload className="h-4 w-4" />
                Import SCCM Export
              </Button>
            )}
            
            <Button
              onClick={() => syncPackagingStatus(false)}
              variant="outline"
              className="flex items-center gap-2 bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
              data-testid="sync-deployments-button"
              title="Sync deployment status from packaged apps"
            >
              <Rocket className="h-4 w-4" />
              Sync Deployments
            </Button>
          </div>
        </div>

        {canImportSccm && (
          <SccmImportDialog
            open={sccmImportOpen}
            onOpenChange={setSccmImportOpen}
            onImported={() => {
              // The estate has been replaced wholesale -- refetch rather
              // than reconcile, mirroring what pollSyncStatus does when an
              // Intune sync completes.
              fetchSyncStatus();
              fetchApps();
              fetchFamilies();
              fetchStats();
              fetchRationalisation();
            }}
          />
        )}

        {/* Sync Completed Banner - Shows briefly after completion */}
        {syncStatus?.status === "completed" && syncStatus?.completed_at && (
          (() => {
            const completedAt = new Date(syncStatus.completed_at);
            const now = new Date();
            const diffMinutes = (now - completedAt) / (1000 * 60);
            const variance = syncStatus.variance;
            // Show for 2 minutes after completion
            if (diffMinutes < 2) {
              return (
                <div className="mb-6 rounded-lg border border-green-500/50 bg-gradient-to-r from-green-900/30 via-slate-800 to-green-900/30 p-4">
                  <div className="flex items-center gap-4">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-400">
                        Sync Complete
                      </p>
                      <p className="text-xs text-slate-300 mt-1">
                        {syncStatus.current_step || "All processing completed successfully."}
                      </p>
                      {variance && (
                        <div className="flex flex-wrap gap-3 mt-2 text-xs">
                          {/* Families and versions are counted separately: a version
                              upgrade used to read as one new app plus one removed. */}
                          {variance.families_new > 0 && (
                            <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                              {variance.families_new} new apps
                            </span>
                          )}
                          {variance.families_removed > 0 && (
                            <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                              {variance.families_removed} apps no longer found
                            </span>
                          )}
                          {variance.versions_new > 0 && (
                            <span className="bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded">
                              {variance.versions_new} versions added
                            </span>
                          )}
                          {variance.versions_removed > 0 && (
                            <span className="bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded">
                              {variance.versions_removed} versions gone
                            </span>
                          )}
                          {variance.versions_increased > 0 && (
                            <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                              ↑ {variance.versions_increased} versions on more devices
                            </span>
                          )}
                          {variance.versions_decreased > 0 && (
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                              ↓ {variance.versions_decreased} versions on fewer devices
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {syncStatus.total_apps || 0} apps • {syncStatus.total_devices || 0} devices
                    </span>
                  </div>
                </div>
              );
            }
            return null;
          })()
        )}
        
        {/* Permission Error Alert */}
        {syncStatus?.status === "failed" && syncStatus?.error?.includes("403") && (
          <Card className="mb-6 border-amber-500/30 bg-amber-500/10">
            <CardContent className="py-4">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-300">
                    Missing Permission
                  </p>
                  <p className="text-sm text-amber-200/80 mt-1">
                    Your Azure App Registration needs the <code className="bg-amber-500/20 px-1 rounded">DeviceManagementManagedDevices.Read.All</code> permission.
                  </p>
                  <Link 
                    to="/settings" 
                    className="text-sm text-amber-400 underline hover:text-amber-300 mt-2 inline-block"
                  >
                    View setup instructions in Settings →
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Stats Cards - Glass Effect */}
        <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-total-apps">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Total Apps</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-400/20">
                <Package className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-4xl font-bold text-white" data-testid="stat-total-apps-value">{statValue('total_apps', stats?.total_apps, (v) => v.toLocaleString())}</span>
              {summaryVarianceBadge('total_apps')}
            </div>
            <div className="text-sm text-white/90">Discovered</div>
          </div>
          
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-rationalised">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Rationalised</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-400/20">
                <Layers className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-4xl font-bold text-white">{rationalisation?.summary?.total_families || 0}</span>
              {allRationalisationFiltersOn && summaryVarianceBadge('rationalised_families')}
            </div>
            <div className="text-sm text-white/90">App Families</div>
          </div>
          
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-devices">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Devices</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/20">
                <HardDrive className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1" data-testid="stat-devices-value">{statValue('total_unique_devices', stats?.total_unique_devices, (v) => v.toLocaleString())}</div>
            <div className="text-sm text-white/90">Total Managed</div>
          </div>
          
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-ready-to-automate">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Ready to Automate</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-400/20">
                <Zap className="w-5 h-5 text-amber-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1" data-testid="stat-ready-to-automate-value">{statValue('apps_with_automation', stats?.apps_with_automation, (v) => v.toLocaleString())}</div>
            <div className="text-sm text-white/90">Automation Available</div>
          </div>
          
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-synced">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Last Synced</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/20">
                <Clock className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <div className="text-lg font-bold text-white mb-1" data-testid="stat-last-synced-value">{
              (!stats || statsError)
                ? "—"
                : (Array.isArray(stats.failed_fields) && stats.failed_fields.includes('sync_status'))
                  ? "—"
                  : formatDate(stats?.last_synced)
            }</div>
            <div className="text-sm text-white/90">Intune Sync</div>
          </div>
        </div>
        
        {/* View Mode Toggle & Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={viewMode === "rationalisation" ? "default" : "outline"}
              onClick={() => { setViewMode("rationalisation"); setPage(1); setFiltersEnabled(true); }}
              data-testid="view-rationalisation-btn"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Rationalisation
            </Button>
            <Button
              variant={viewMode === "apps" ? "default" : "outline"}
              onClick={() => { setViewMode("apps"); setPage(1); }}
              data-testid="view-apps-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              All Apps
            </Button>
            <Button
              variant={viewMode === "vulnerabilities" ? "default" : "outline"}
              onClick={() => { setViewMode("vulnerabilities"); setPage(1); }}
              data-testid="view-vulnerabilities-btn"
              className={viewMode === "vulnerabilities" ? "" : "border-red-400 text-red-400 hover:bg-red-900/20"}
            >
              <ShieldAlert className="h-4 w-4 mr-2" />
              Vulnerabilities
              {vulnStats?.total_vulnerable > 0 && (
                <Badge className="ml-2 bg-red-600 text-white text-xs">{vulnStats.total_vulnerable}</Badge>
              )}
            </Button>
            <Button
              variant={viewMode === "upgrades" ? "default" : "outline"}
              onClick={() => { setViewMode("upgrades"); setPage(1); }}
              data-testid="view-upgrades-btn"
              className={viewMode === "upgrades" ? "" : "border-green-400 text-green-400 hover:bg-green-900/20"}
            >
              <ArrowUpCircle className="h-4 w-4 mr-2" />
              Upgrades
              {upgradeStats?.total_upgradable > 0 && (
                <Badge className="ml-2 bg-green-600 text-white text-xs">{upgradeStats.total_upgradable}</Badge>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/catalog")}
              data-testid="view-catalog-btn"
              className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              <Book className="h-4 w-4 mr-2" />
              Catalog
            </Button>
          </div>
          
          {viewMode !== "rationalisation" && (
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#220175]/50" />
              <Input
                type="text"
                placeholder="Search apps..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white text-[#220175] placeholder:text-[#220175]/50"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-button" className="bg-[#E5EFFA] text-[#006AF2] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={exportToCSV}
              disabled={loading || exporting || (viewMode === "apps" ? total === 0 : families.length === 0)}
              data-testid="export-csv-btn"
              className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              {viewMode === "apps" && exporting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </>
              )}
            </Button>
          </form>
          )}
        </div>
        
        {/* AppsDatabase Import & Filters Panel */}
        {(viewMode === "families" || viewMode === "rationalisation") && (
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl mb-6">
            <div className="py-4 px-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* AppsDatabase Status */}
                <div className="flex items-center gap-3">
                  <Database className="h-5 w-5 text-white/90" />
                  <div className="text-sm">
                    {appsDbStats?.imported ? (
                      <span className="text-green-400">
                        ALICE AppsDatabase successfully loaded
                      </span>
                    ) : (
                      <Link to="/settings" className="text-amber-400 hover:text-amber-300 underline">
                        AppsDatabase: Not imported - Go to Settings to import
                      </Link>
                    )}
                  </div>
                </div>
                
                {/* Divider */}
                <div className="hidden lg:block w-px h-8 bg-gray-700" />
                
                {/* Filter Toggles */}
                {appsDbStats?.imported && (
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-white/90" />
                      <span className="text-sm text-white/90">Filters:</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-system"
                        checked={filters.excludeSystem}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeSystem: checked }))}
                      />
                      <Label htmlFor="exclude-system" className="text-sm text-white/90 cursor-pointer">
                        System Components
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-drivers"
                        checked={filters.excludeDrivers}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeDrivers: checked }))}
                      />
                      <Label htmlFor="exclude-drivers" className="text-sm text-white/90 cursor-pointer">
                        Device Drivers
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-nonbusiness"
                        checked={filters.excludeNonBusiness}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeNonBusiness: checked }))}
                      />
                      <Label htmlFor="exclude-nonbusiness" className="text-sm text-white/90 cursor-pointer">
                        Non-Business
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-helpfiles"
                        checked={filters.excludeHelpFiles}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeHelpFiles: checked }))}
                      />
                      <Label htmlFor="exclude-helpfiles" className="text-sm text-white/90 cursor-pointer">
                        Help Files
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-redistributable"
                        checked={filters.excludeRedistributable}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeRedistributable: checked }))}
                      />
                      <Label htmlFor="exclude-redistributable" className="text-sm text-white/90 cursor-pointer">
                        Redistributables
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-deprecated"
                        checked={filters.excludeDeprecated}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeDeprecated: checked }))}
                      />
                      <Label htmlFor="exclude-deprecated" className="text-sm text-white/90 cursor-pointer">
                        Deprecated
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-installation-files"
                        checked={filters.excludeInstallationFiles}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeInstallationFiles: checked }))}
                      />
                      <Label htmlFor="exclude-installation-files" className="text-sm text-white/90 cursor-pointer">
                        Installation Files
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="exclude-bundle"
                        checked={filters.excludeBundle}
                        onCheckedChange={(checked) => setFilters(f => ({ ...f, excludeBundle: checked }))}
                      />
                      <Label htmlFor="exclude-bundle" className="text-sm text-white/90 cursor-pointer">
                        Bundles
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Rationalisation View */}
        {viewMode === "rationalisation" && (
          <div className="space-y-6">
            {/* Rationalisation Summary Cards - only show if we have data */}
            {rationalisation && (
            <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-emerald-500/30 rounded-xl p-6" data-testid="card-worth-packaging">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-400/20">
                    <Layers className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-white/90">Apps with 10+ Installs</p>
                    {/* Bands and total both come from the rationalisation summary,
                        the same family list and distinct-device figure as the
                        "Rationalised" tile and the CSV export, so the three bands
                        sum to that tile. They used to come from /discover/stats,
                        which banded a differently filtered set. */}
                    {rationalisation?.summary?.footprint ? (
                      <>
                        <p className="text-2xl font-bold text-white">
                          {rationalisation.summary.footprint.footprint_widespread.toLocaleString()}
                          <span className="text-lg font-normal text-white/70"> of {(rationalisation.summary.total_families || 0).toLocaleString()}</span>
                        </p>
                        <p className="text-xs text-white/80">
                          {rationalisation.summary.footprint.footprint_few.toLocaleString()} on 2–9 ·{" "}
                          {rationalisation.summary.footprint.footprint_single.toLocaleString()} one-offs — all packageable through ALICE
                        </p>
                      </>
                    ) : (
                      // Still loading, or an older cached summary without bands.
                      <p className="text-2xl font-bold text-white">—</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-amber-500/30 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-400/20">
                    <AlertTriangle className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white/90">Families Needing Updates</p>
                    <p className="text-2xl font-bold text-white">
                      {rationalisation.summary?.families_needing_update || 0}
                    </p>
                    <p className="text-xs text-white/80">
                      out of {rationalisation.summary?.families_with_multiple_versions || 0} with multiple versions
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-purple-500/30 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-400/20">
                    <Package className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white/90">Managed Apps</p>
                    <p className="text-2xl font-bold text-white">
                      {stats?.managed_apps_count || 0} <span className="text-lg font-normal text-white/70">of {rationalisation?.summary?.total_families || 0}</span>
                    </p>
                    <p className="text-xs text-white/80">
                      packaged/deployed through ALICE
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-cyan-500/30 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-400/20">
                    <TrendingUp className="h-6 w-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white/90">Overall Update Coverage</p>
                    <p className="text-2xl font-bold text-white">
                      {rationalisation?.summary?.overall_update_coverage_pct || 0}%
                    </p>
                    <p className="text-xs text-white/80">
                      apps running latest version
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}
            
            {/* App Category Filter */}
            <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl">
              <div className="py-4 px-6">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-white/90" />
                    <span className="text-sm text-white/90 font-medium">App Category</span>
                  </div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setPage(1);
                    }}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white text-sm rounded-md px-3 py-1.5 focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-w-[200px]"
                    data-testid="category-filter"
                  >
                    <option value="" className="bg-[#1a2332] text-white">All Categories</option>
                    {appCategories.map((cat) => (
                      <option key={cat.category} value={cat.category} className="bg-[#1a2332] text-white">
                        {cat.category} ({cat.count})
                      </option>
                    ))}
                  </select>
                  {selectedCategory && (
                    <button
                      onClick={() => {
                        setSelectedCategory("");
                        setPage(1);
                      }}
                      className="text-xs text-white/80 hover:text-white flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Clear filter
                    </button>
                  )}
                  {selectedCategory && (
                    <span className="text-xs text-blue-400 ml-2">
                      Showing apps in "{selectedCategory}" category
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Management Filter Toggle */}
            <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-white/80 font-medium mr-1">View:</span>
                {[
                  { key: "all", label: "All Apps", icon: Layers },
                  { key: "managed", label: "Managed", icon: CheckCircle },
                  { key: "unmanaged", label: "Unmanaged", icon: AlertCircle },
                  { key: "out_of_scope", label: "Out of Scope", icon: EyeOff },
                  { key: "rationalised", label: "Rationalised", icon: Recycle },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => { setManagementFilter(key); setPage(1); }}
                    disabled={initialSyncing}
                    data-testid={`management-filter-${key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      initialSyncing ? "opacity-50 cursor-not-allowed " : ""
                    }${
                      managementFilter === key
                        ? key === "managed" ? "bg-green-600/30 text-green-300 border border-green-500/50 shadow-[0_0_8px_rgba(34,197,94,0.15)]"
                        : key === "unmanaged" ? "bg-amber-600/30 text-amber-300 border border-amber-500/50 shadow-[0_0_8px_rgba(245,158,11,0.15)]"
                        : key === "out_of_scope" ? "bg-slate-600/30 text-slate-300 border border-slate-500/50"
                        : key === "rationalised" ? "bg-fuchsia-600/30 text-fuchsia-300 border border-fuchsia-500/50"
                        : "bg-cyan-600/30 text-cyan-300 border border-cyan-500/50 shadow-[0_0_8px_rgba(6,182,212,0.15)]"
                        : "bg-white/[0.04] text-white/60 border border-transparent hover:bg-white/[0.08] hover:text-white/80"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
                {initialSyncing && (
                  <span className="ml-2 inline-flex items-center gap-2 text-xs text-white/70" data-testid="rationalisation-sync-hint">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Syncing deployment status…
                  </span>
                )}
              </div>
            </div>
            
            {/* Rationalised App Families Table */}
            <div className="relative bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden">
              {initialSyncing && (
                <div
                  className="absolute inset-0 z-10 bg-[rgba(10,15,30,0.6)] backdrop-blur-[2px] flex flex-col items-center justify-center py-16"
                  data-testid="rationalisation-sync-overlay"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-cyan-300 mb-3" />
                  <p className="text-white font-medium">Reconciling deployment status…</p>
                  <p className="text-xs text-white/60 mt-1 max-w-sm text-center px-4">
                    Making sure Managed / Unmanaged / Out of Scope counts are accurate before you pick a category.
                  </p>
                </div>
              )}
              <div className="p-6 border-b border-[rgba(229,239,250,0.1)]">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-lg font-semibold text-white">
                    <Layers className="h-5 w-5" />
                    Rationalised Applications ({families.length > 0 ? total.toLocaleString() : 0})
                  </span>
                  <div className="flex items-center gap-2">
                    <form onSubmit={handleSearch} className="flex gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/70" />
                        <Input
                          type="text"
                          placeholder="Search apps..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="pl-10 w-64 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/70"
                        />
                      </div>
                      <Button type="submit" variant="outline" size="sm" className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">Search</Button>
                    </form>
                    <Button 
                      type="button" 
                      variant="default" 
                      size="sm"
                      onClick={() => navigate("/catalog")}
                      data-testid="add-new-app-btn"
                      className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add New App
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={exportRationalisedToCSV}
                      disabled={families.length === 0 || exporting}
                      data-testid="export-rationalised-csv-btn"
                      className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                    >
                      {exporting ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-1" />
                          Export All CSV
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="p-6 pt-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-white" />
                  </div>
                ) : families.length === 0 ? (
                  <div className="text-center py-12">
                    <Layers className="h-12 w-12 mx-auto text-[#E5EFFA]/30 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No App Families Found</h3>
                    <p className="text-white/90">Adjust your filters or sync apps from Intune.</p>
                  </div>
                ) : (
                  <>
                    {/* Sort indicator */}
                    <div className="flex items-center gap-2 mb-3 text-xs text-white/90">
                      <ArrowUpDown className="h-3 w-3" />
                      <span>Click column headers to sort</span>
                      <span className="text-white/50">·</span>
                      <button
                        type="button"
                        onClick={() => { setViewMode("vulnerabilities"); setPage(1); }}
                        className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline"
                        title="CVE matches, ALICE posture and the Fix Queue live on the Vulnerabilities tab"
                        data-testid="rationalised-vuln-link"
                      >
                        <Shield className="h-3 w-3" /> Vulnerabilities are on the Vulnerabilities tab
                      </button>
                      {sortConfig.key && (
                        <Badge variant="outline" className="text-xs text-white/90 border-[rgba(229,239,250,0.1)]">
                          Sorted by {sortConfig.key === 'family_name' ? 'Name' : 
                                    sortConfig.key === 'device_count' ? 'Devices' :
                                    sortConfig.key === 'version_count' ? 'Versions' :
                                    sortConfig.key === 'status' ? 'Status' :
                                    sortConfig.key === 'criticality' ? 'Criticality' :
                                    sortConfig.key} ({sortConfig.direction === 'asc' ? '↑' : '↓'})
                        </Badge>
                      )}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableHeader column="family_name" label="Application Family" />
                          <SortableHeader column="version_count" label="Versions" />
                          <SortableHeader column="status" label="Status" className="text-center" />
                          <SortableHeader column="device_count" label="Device Count" className="text-right" />
                          <TableHead className="text-white font-semibold">Actions</TableHead>
                          <SortableHeader column="criticality" label="Business Criticality" className="text-center" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getSortedFamilies().map((family, idx) => (
                          <TableRow 
                            key={family.family_name || idx} 
                            data-testid={`family-row-${idx}`}
                            className="hover:bg-white/[0.04] cursor-pointer"
                            onClick={() => navigateToGovernance(family.family_name)}
                          >
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-white hover:text-white">{family.family_name}</p>
                                  {family.out_of_scope && (
                                    <Badge variant="outline" className="text-xs text-slate-400 border-slate-500/50 bg-slate-700/30">
                                      <EyeOff className="h-3 w-3 mr-1" />
                                      Out of Scope
                                    </Badge>
                                  )}
                                </div>
                                {family.category && (
                                  <Badge variant="outline" className="text-xs text-white/90 mt-1">
                                    {family.category}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {family.versions?.slice(0, 3).map((v, i) => (
                                  <Badge key={i} variant="outline" className="text-xs text-white border-2 border-blue-900 bg-blue-950/40">
                                    {typeof v === 'object' ? v.version : v}
                                  </Badge>
                                ))}
                                {family.versions?.length > 3 && (
                                  <Badge variant="outline" className="text-xs text-white/80 border-2 border-blue-900 bg-blue-950/40">
                                    +{family.versions.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                              {(() => {
                                // Rationalised to another family (App
                                // Governance): shown first, so a family that
                                // is meant to be gone but still has devices
                                // stands out. The device count on the row is
                                // what is left to remove.
                                if (family.rationalised_to) {
                                  return (
                                    <div className="flex flex-col items-center gap-0.5" data-testid={`rationalised-badge-${idx}`}>
                                      <Badge className="bg-fuchsia-600/20 text-fuchsia-300 border border-fuchsia-500 text-xs">
                                        <Recycle className="h-3 w-3 mr-1" />
                                        Rationalised
                                      </Badge>
                                      <span className="text-[10px] text-white/70">→ {family.rationalised_to}</span>
                                    </div>
                                  );
                                }
                                const status = family.packaging_status?.status;
                                const isUninstallOnly = family.packaging_status?.uninstall_only;
                                // Uninstall-only packages travel the same
                                // test -> phase 1/2/3 path, but "Deployed" and
                                // "LIVE" would read as though the app had been
                                // rolled out. Give the removal lane its own
                                // wording so the table never states the
                                // opposite of what happened.
                                if (isUninstallOnly) {
                                  if (status === "live") {
                                    return <Badge className="bg-red-700 text-white text-xs">Removed</Badge>;
                                  } else if (status?.startsWith("live_phase")) {
                                    const p = family.packaging_status?.current_phase || parseInt(status.replace("live_phase", ""), 10) || 1;
                                    const pName = liveStageInfo[familyStageKey(family)]?.names?.[p - 1] || `Phase ${p}`;
                                    return <Badge className="bg-red-600 text-white text-xs">Removing - {pName}</Badge>;
                                  } else if (status === "approved") {
                                    return <Badge className="bg-green-600 text-white text-xs animate-pulse">✓ Ready to Remove</Badge>;
                                  } else if (status === "deployed_test") {
                                    return <Badge className="bg-orange-600 text-white text-xs">Uninstall - Test</Badge>;
                                  } else if (status === "packaged") {
                                    return <Badge className="bg-red-600/20 text-red-300 border border-red-500 text-xs">Uninstall - Packaged</Badge>;
                                  }
                                }
                                if (status === "live") {
                                  return <Badge className="bg-purple-600 text-white text-xs">LIVE</Badge>;
                                } else if (status?.startsWith("live_phase")) {
                                  const p = family.packaging_status?.current_phase || parseInt(status.replace("live_phase", ""), 10) || 1;
                                  const pName = liveStageInfo[familyStageKey(family)]?.names?.[p - 1] || `Phase ${p}`;
                                  return <Badge className="bg-blue-600 text-white text-xs">LIVE - {pName}</Badge>;
                                } else if (status === "approved") {
                                  return <Badge className="bg-green-600 text-white text-xs animate-pulse">✓ Ready for Go Live</Badge>;
                                } else if (status === "deployed_test") {
                                  return <Badge className="bg-orange-600 text-white text-xs">Deployed</Badge>;
                                } else if (status === "packaged") {
                                  return <Badge className="bg-blue-600 text-white text-xs">Packaged</Badge>;
                                } else if (family.winget_matched) {
                                  return <Badge className="bg-green-600/20 text-green-400 border border-green-500 text-xs">Automation Available</Badge>;
                                } else if (family.private_repo_available) {
                                  return <Badge className="bg-emerald-600/20 text-emerald-400 border border-emerald-500 text-xs" data-testid={`private-repo-badge-${idx}`}>Private Repo · v{family.private_repo_latest_version}</Badge>;
                                } else {
                                  return <Badge variant="outline" className="text-white/90 text-xs">Manual</Badge>;
                                }
                              })()}
                            </TableCell>
                            <TableCell className="text-right font-medium text-white">
                              <div className="flex items-center justify-end gap-2">
                                <span>{family.unique_device_count?.toLocaleString() || family.total_device_count?.toLocaleString() || 0}</span>
                                {family.total_variance != null && family.total_variance !== 0 && (
                                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                    family.total_variance > 0 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {family.total_variance > 0 ? '↑' : '↓'}{Math.abs(family.total_variance)}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {(() => {
                                const status = family.packaging_status?.status;
                                const currentPhase = family.packaging_status?.current_phase;
                                // See the Status column above: the removal lane
                                // reuses the phase machinery but never the
                                // install wording.
                                const isUninstallOnly = family.packaging_status?.uninstall_only;
                                
                                // LIVE Phases - Show phase status with proceed button
                                if (status?.startsWith("live_phase") || status === "live") {
                                  // Complete is the "live" status (the rollout's
                                  // last stage done), never "phase 3": a
                                  // pipeline has 1-10 stages after UAT.
                                  const isComplete = status === "live";
                                  const phase = currentPhase || parseInt(status.replace("live_phase", ""), 10) || 1;
                                  const stageInfo = liveStageInfo[familyStageKey(family)];
                                  const phaseLabelText = stageInfo?.names?.[phase - 1] || `Phase ${phase}`;
                                  const nextStageName = stageInfo?.names?.[phase] || "Next stage";
                                  // Hide proceed at the last stage once the
                                  // count is known (the backend refuses it).
                                  const hasNextStage = !stageInfo?.count || phase < stageInfo.count;
                                  return (
                                    <div className="flex gap-1 flex-wrap items-center">
                                      <Badge className={`${isComplete ? (isUninstallOnly ? 'bg-red-700' : 'bg-purple-600') : (isUninstallOnly ? 'bg-red-600' : 'bg-blue-600')} text-white text-xs`}>
                                        {isUninstallOnly ? <Trash2 className="h-3 w-3 mr-1" /> : <Rocket className="h-3 w-3 mr-1" />}
                                        {isComplete
                                          ? (isUninstallOnly ? 'Removed' : 'LIVE')
                                          : (isUninstallOnly ? `Removing - ${phaseLabelText}` : `LIVE - ${phaseLabelText}`)}
                                      </Badge>
                                      {!isComplete && hasNextStage && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleGoLive(family)}
                                          className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold text-xs"
                                          data-testid={`proceed-phase-${idx}`}
                                          disabled={!canDeployPackages}
                                          title={!canDeployPackages ? "You don't have permission to deploy packages" : ""}
                                        >
                                          <ChevronRight className="h-3 w-3 mr-1" />
                                          {nextStageName}
                                        </Button>
                                      )}
                                    </div>
                                  );
                                }
                                
                                // Deployed to Test - Show Go Live button
                                if (status === "deployed_test" || status === "approved") {
                                  const isApproved = status === "approved" || family.packaging_status?.all_testers_approved;
                                  return (
                                    <div className="flex gap-1 flex-wrap">
                                      <Badge className={`${isApproved ? 'bg-green-600/20 text-green-400 border border-green-500' : 'bg-orange-600/20 text-orange-400 border border-orange-500'} text-xs`}>
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        {isApproved ? 'Approved' : (isUninstallOnly ? 'Uninstall - Test' : 'Test')}
                                      </Badge>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleGoLive(family)}
                                        className={`${isApproved ? 'bg-green-500 text-white border-green-600 hover:bg-green-600 font-semibold animate-pulse' : 'bg-purple-500 text-white border-purple-600 hover:bg-purple-600 font-semibold'} text-xs`}
                                        data-testid={`go-live-family-${idx}`}
                                        disabled={!canDeployPackages}
                                        title={!canDeployPackages ? "You don't have permission to deploy packages" : ""}
                                      >
                                        <Rocket className="h-3 w-3 mr-1" />
                                        {isUninstallOnly
                                          ? (isApproved ? 'Remove Now!' : 'Start Removal')
                                          : (isApproved ? '🎉 Go Live Now!' : 'Go Live')}
                                      </Button>
                                    </div>
                                  );
                                }
                                
                                // Packaged but not deployed - Show Deploy to Test button
                                if (status === "packaged") {
                                  return (
                                    <div className="flex gap-1 flex-wrap">
                                      <Badge className={`${isUninstallOnly ? 'bg-red-600/20 text-red-300 border border-red-500' : 'bg-blue-600/20 text-blue-400 border border-blue-500'} text-xs`}>
                                        {isUninstallOnly ? <Trash2 className="h-3 w-3 mr-1" /> : <Package className="h-3 w-3 mr-1" />}
                                        {isUninstallOnly ? 'Uninstall - Packaged' : 'Packaged'}
                                      </Badge>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          toast.info("Deploy to Test coming soon - use Packages page for now");
                                        }}
                                        className="bg-orange-500 text-white border-orange-600 hover:bg-orange-600 font-semibold text-xs"
                                        data-testid={`deploy-test-family-${idx}`}
                                        disabled={!canDeployPackages}
                                        title={!canDeployPackages ? "You don't have permission to deploy packages" : ""}
                                      >
                                        <Rocket className="h-3 w-3 mr-1" />
                                        Deploy Test
                                      </Button>
                                    </div>
                                  );
                                }
                                
                                // Not packaged - Show Package or Custom Upload,
                                // each with the "Uninstall only" alternative
                                // behind the split-button caret.
                                if (family.winget_matched) {
                                  return (
                                    <PackageSplitButton
                                      idx={idx}
                                      label="Package"
                                      icon={Package}
                                      primaryClassName="bg-green-500 text-white border-green-600 hover:bg-green-600"
                                      caretClassName="bg-green-500 text-white border-green-600 hover:bg-green-600"
                                      onPrimary={() => handlePackageApp(family)}
                                      onUninstallOnly={() => handleUninstallOnly(family)}
                                      primaryMenuLabel="Package (install)"
                                      testId={`package-family-${idx}`}
                                      disabled={!canCreatePackages}
                                      title={!canCreatePackages ? "You don't have permission to create packages" : ""}
                                    />
                                  );
                                }

                                // Private Repository match: replaces "Custom Upload" with "Package"
                                // (per design doc §6.2). Latest version is picked from the blob.
                                if (family.private_repo_available) {
                                  return (
                                    <PackageSplitButton
                                      idx={idx}
                                      label="Package"
                                      icon={Package}
                                      primaryClassName="bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600"
                                      caretClassName="bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600"
                                      onPrimary={() => handlePackageFromPrivateRepo(family)}
                                      onUninstallOnly={() => handleUninstallOnly(family)}
                                      primaryMenuLabel={`Package v${family.private_repo_latest_version} (install)`}
                                      testId={`package-private-repo-${idx}`}
                                      disabled={!canCreatePackages}
                                      title={!canCreatePackages
                                        ? "You don't have permission to create packages"
                                        : `Package latest version (v${family.private_repo_latest_version}) from Private Repository`}
                                    />
                                  );
                                }
                                
                                return (
                                  <PackageSplitButton
                                    idx={idx}
                                    label="Custom"
                                    icon={Upload}
                                    primaryClassName="bg-blue-500 text-white border-blue-600 hover:bg-blue-600"
                                    caretClassName="bg-blue-500 text-white border-blue-600 hover:bg-blue-600"
                                    onPrimary={() => handleCustomUpload(family)}
                                    onUninstallOnly={() => handleUninstallOnly(family)}
                                    primaryMenuLabel="Custom upload (install)"
                                    testId={`custom-upload-family-${idx}`}
                                    disabled={!canCreatePackages}
                                    title={!canCreatePackages ? "You don't have permission to create packages" : ""}
                                  />
                                );
                              })()}
                              {/* Out of Scope toggle - show for unmanaged apps or currently out-of-scope */}
                              {(!family.packaging_status?.status || family.out_of_scope) && (
                                <button
                                  onClick={(e) => toggleOutOfScope(family.family_name, e)}
                                  className={`ml-1 p-1.5 rounded-md transition-all ${
                                    family.out_of_scope 
                                      ? 'bg-slate-600/30 text-slate-300 hover:bg-cyan-600/30 hover:text-cyan-300' 
                                      : 'text-white/30 hover:text-slate-300 hover:bg-slate-600/20'
                                  }`}
                                  title={family.out_of_scope ? "Mark as In Scope" : "Mark as Out of Scope"}
                                  data-testid={`out-of-scope-${idx}`}
                                >
                                  {family.out_of_scope ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                              )}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => updateRiskLevel(family.family_name, 'green')}
                                  className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                                    family.risk_level === 'green' 
                                      ? 'bg-green-500 border-green-400 ring-2 ring-green-400/50' 
                                      : 'bg-green-500/30 border-green-500/50 hover:bg-green-500/60'
                                  }`}
                                  title="Low Criticality"
                                  data-testid={`risk-green-${idx}`}
                                />
                                <button
                                  onClick={() => updateRiskLevel(family.family_name, 'amber')}
                                  className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                                    family.risk_level === 'amber' 
                                      ? 'bg-amber-500 border-amber-400 ring-2 ring-amber-400/50' 
                                      : 'bg-amber-500/30 border-amber-500/50 hover:bg-amber-500/60'
                                  }`}
                                  title="Medium Criticality"
                                  data-testid={`risk-amber-${idx}`}
                                />
                                <button
                                  onClick={() => updateRiskLevel(family.family_name, 'red')}
                                  className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                                    family.risk_level === 'red' 
                                      ? 'bg-red-500 border-red-400 ring-2 ring-red-400/50' 
                                      : 'bg-red-500/30 border-red-500/50 hover:bg-red-500/60'
                                  }`}
                                  title="High Criticality"
                                  data-testid={`risk-red-${idx}`}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-[rgba(229,239,250,0.1)]">
                      <p className="text-sm text-white/90">
                        Showing {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newPage = Math.max(1, page - 1);
                            setPage(newPage);
                            fetchFilteredFamilies(newPage);
                          }}
                          disabled={page === 1}
                          data-testid="rationalisation-prev-page"
                          className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-white/90">
                          Page {page} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newPage = Math.min(totalPages, page + 1);
                            setPage(newPage);
                            fetchFilteredFamilies(newPage);
                          }}
                          disabled={page === totalPages}
                          data-testid="rationalisation-next-page"
                          className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Apps Table */}
        {viewMode === "apps" && (
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden">
            <div className="p-6 border-b border-[rgba(229,239,250,0.1)]">
              <span className="text-lg font-semibold text-white">Detected Applications ({total.toLocaleString()})</span>
            </div>
            <div className="p-6 pt-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-cyan-400" />
                </div>
              ) : apps.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 mx-auto text-[#E5EFFA]/30 mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Apps Found</h3>
                  <p className="text-gray-300 mb-4">
                    {stats?.total_apps === 0 
                      ? "Click 'Sync from Intune' to discover apps on your managed devices."
                      : "No apps match your search criteria."}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white font-semibold">Application Name</TableHead>
                        <TableHead className="text-white font-semibold">Version</TableHead>
                        <TableHead className="text-white font-semibold">Publisher</TableHead>
                        <TableHead className="text-gray-200 font-semibold text-center">CVE Risk</TableHead>
                        <TableHead className="text-gray-200 font-semibold text-right">Device Count</TableHead>
                        <TableHead className="text-white font-semibold">Size</TableHead>
                        <TableHead className="text-gray-200 font-semibold w-20">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apps.map((app, idx) => (
                        <TableRow 
                          key={app.app_id || idx} 
                          data-testid={`app-row-${idx}`}
                          className="hover:bg-white/[0.04] cursor-pointer"
                          onClick={() => openDeviceDetails(app)}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium text-white">{app.display_name}</p>
                              <p className="text-xs text-white/90">{app.display_name_normalized}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-white border-2 border-blue-900 bg-blue-950/40">{app.version || "-"}</Badge>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {app.publisher || "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            {app.cve_data?.matched ? (
                              <button
                                onClick={(e) => openCveDetails(app, e)}
                                className="flex items-center justify-center gap-1 hover:opacity-80 transition-opacity"
                                title="Click to view CVE details"
                              >
                                {getSeverityBadge(app.cve_data.highest_severity)}
                                <span className="text-xs text-white/90">({app.cve_data.cve_count})</span>
                                <ExternalLink className="h-3 w-3 text-white/70" />
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium text-white">
                            <div className="flex items-center justify-end gap-2">
                              <span>{app.device_count?.toLocaleString() || 0}</span>
                              {app.variance?.device_count_change !== undefined && app.variance.device_count_change !== 0 && (
                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                  app.variance.device_count_change > 0 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {app.variance.device_count_change > 0 ? '↑' : '↓'}{Math.abs(app.variance.device_count_change)}
                                </span>
                              )}
                              {app.variance?.is_new && (
                                <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                                  NEW
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {formatSize(app.size_in_byte)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeviceDetails(app);
                                }}
                                className="text-gray-300 hover:text-white"
                                data-testid={`view-devices-${idx}`}
                              >
                                <Monitor className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[rgba(229,239,250,0.1)]">
                    <p className="text-sm text-white/90">
                      Showing {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newPage = Math.max(1, page - 1);
                          setPage(newPage);
                          fetchApps(newPage);
                        }}
                        disabled={page === 1}
                        data-testid="apps-prev-page"
                        className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-white/90">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newPage = Math.min(totalPages, page + 1);
                          setPage(newPage);
                          fetchApps(newPage);
                        }}
                        disabled={page === totalPages}
                        data-testid="apps-next-page"
                        className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Families Table */}
        {viewMode === "families" && (
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden">
            <div className="p-6 border-b border-[rgba(229,239,250,0.1)]">
              <span className="text-lg font-semibold text-white">Application Families ({total.toLocaleString()})</span>
            </div>
            <div className="p-6 pt-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-cyan-400" />
                </div>
              ) : families.length === 0 ? (
                <div className="text-center py-12">
                  <Layers className="h-12 w-12 mx-auto text-[#E5EFFA]/30 mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Families Found</h3>
                  <p className="text-white/90 mb-4">
                    {stats?.total_families === 0 
                      ? "Click 'Sync from Intune' to discover apps on your managed devices."
                      : "No families match your search criteria."}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white font-semibold">Application Family</TableHead>
                        <TableHead className="text-white font-semibold">Publisher</TableHead>
                        <TableHead className="text-gray-200 font-semibold text-center">Criticality</TableHead>
                        <TableHead className="text-gray-200 font-semibold text-center">Versions</TableHead>
                        <TableHead className="text-white font-semibold">Highest Version</TableHead>
                        <TableHead className="text-white font-semibold">Most Installed</TableHead>
                        <TableHead className="text-gray-200 font-semibold text-right">Update Coverage</TableHead>
                        <TableHead className="text-gray-200 font-semibold text-right">Total Devices</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {families.map((family, idx) => (
                        <TableRow key={family.family_name || idx} data-testid={`family-row-${idx}`} className="hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]">
                          <TableCell>
                            <p className="font-medium text-white">{family.family_name}</p>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {family.publisher || "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            {family.risk_level ? (
                              <div className="flex justify-center">
                                <div 
                                  className={`w-4 h-4 rounded-full ${
                                    family.risk_level === 'green' ? 'bg-green-500' :
                                    family.risk_level === 'amber' ? 'bg-amber-500' :
                                    family.risk_level === 'red' ? 'bg-red-500' : 'bg-gray-500'
                                  }`}
                                  title={`Criticality: ${family.risk_level === 'green' ? 'Low' : family.risk_level === 'amber' ? 'Medium' : family.risk_level === 'red' ? 'High' : 'Not set'}`}
                                />
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{family.version_count}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-900/30 text-green-300 border-green-600">
                              {family.highest_version || "-"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-white border-2 border-blue-900 bg-blue-950/40">{family.most_installed_version || "-"}</Badge>
                              <span className="text-xs text-white/90">
                                ({family.most_installed_count?.toLocaleString() || 0})
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {family.has_multiple_versions ? (
                              <div className="flex items-center justify-end gap-2">
                                <span className={`font-medium ${family.update_coverage_pct >= 90 ? 'text-green-400' : family.update_coverage_pct >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                                  {family.update_coverage_pct || 0}%
                                </span>
                                {family.needs_consolidation && (
                                  <AlertTriangle className="h-4 w-4 text-amber-400" title="Needs consolidation" />
                                )}
                              </div>
                            ) : (
                              <span className="text-green-400 flex items-center justify-end gap-1">
                                <CheckCircle className="h-4 w-4" />
                                100%
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium text-white">
                            <div className="flex items-center justify-end gap-2">
                              <span>{family.unique_device_count?.toLocaleString() || family.total_device_count?.toLocaleString() || 0}</span>
                              {family.total_variance != null && family.total_variance !== 0 && (
                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                  family.total_variance > 0 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {family.total_variance > 0 ? '↑' : '↓'}{Math.abs(family.total_variance)}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[rgba(229,239,250,0.1)]">
                    <p className="text-sm text-white/90">
                      Showing {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newPage = Math.max(1, page - 1);
                          setPage(newPage);
                          fetchFamilies(newPage);
                        }}
                        disabled={page === 1}
                        data-testid="families-prev-page"
                        className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-white/90">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newPage = Math.min(totalPages, page + 1);
                          setPage(newPage);
                          fetchFamilies(newPage);
                        }}
                        disabled={page === totalPages}
                        data-testid="families-next-page"
                        className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Vulnerabilities View */}
        {viewMode === "vulnerabilities" && (
          <div className="space-y-2">
            {/* Embed the new CPE-based CVE management directly inside the
                Discover tab so users keep the Discover stats + tab bar in
                view while reviewing CVEs (no separate page navigation). */}
            <CVEManagementPage embedded />
          </div>
        )}
        
        {/* Upgrades View */}
        {viewMode === "upgrades" && (
          <div className="space-y-6">
            {/* Winget Database Status */}
            {!wingetDbStats?.imported && (
              <div className="bg-amber-500/20 backdrop-blur-sm border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-200">
                      Winget Database Not Imported
                    </p>
                    <p className="text-sm text-amber-300/80 mt-1">
                      Import a Winget database in Settings to see upgrade recommendations.
                    </p>
                    <Link 
                      to="/settings" 
                      className="text-sm text-amber-300 underline hover:text-amber-200 mt-2 inline-block"
                    >
                      Go to Settings →
                    </Link>
                  </div>
                </div>
              </div>
            )}
            
            {/* Upgrade Summary Cards */}
            {wingetDbStats?.imported && (
              <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-green-500/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <ArrowUpCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-white/90">Apps with Upgrades</p>
                        <p className="text-2xl font-bold text-green-400">
                          {upgradeStats?.total_upgradable || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-500/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Package className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-white/90">Matched to Winget</p>
                        <p className="text-2xl font-bold text-blue-400">
                          {upgradeStats?.total_matched || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-purple-500/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <Database className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-white/90">Winget Packages</p>
                        <p className="text-2xl font-bold text-purple-400">
                          {wingetDbStats?.total?.toLocaleString() || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Upgradable Apps Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-white">
                  <span className="flex items-center gap-2">
                    <ArrowUpCircle className="h-5 w-5" />
                    Applications with Available Upgrades ({total.toLocaleString()})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-white/90" />
                  </div>
                ) : upgradableApps.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">All Apps Up to Date</h3>
                    <p className="text-gray-300 mb-4">
                      {wingetDbStats?.imported 
                        ? "No applications have newer versions available in Winget."
                        : "Import a Winget database in Settings to check for upgrades."}
                    </p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-white font-semibold">Application Name</TableHead>
                          <TableHead className="text-white font-semibold">Current Version</TableHead>
                          <TableHead className="text-white font-semibold">Latest Version</TableHead>
                          <TableHead className="text-white font-semibold">Winget Package</TableHead>
                          <TableHead className="text-gray-200 font-semibold text-right">Affected Devices</TableHead>
                          <TableHead className="text-white font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {upgradableApps.map((app, idx) => (
                          <TableRow 
                            key={app.app_id || idx} 
                            data-testid={`upgrade-app-row-${idx}`}
                            className="hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]"
                          >
                            <TableCell>
                              <div>
                                <p className="font-medium text-white">{app.display_name}</p>
                                <p className="text-xs text-white/90">{app.display_name_normalized}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-red-300 border-2 border-red-800 bg-red-950/40">{app.winget_data?.current_version || app.version || "-"}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-600 text-white">{app.winget_data?.latest_version || "-"}</Badge>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-white/90">{app.winget_data?.package_identifier || "-"}</p>
                              <p className="text-xs text-white/80">{app.winget_data?.installer_type || ""}</p>
                            </TableCell>
                            <TableCell className="text-right font-medium text-white">
                              {app.device_count?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/", { 
                                    state: { 
                                      autoPackage: true,
                                      searchQuery: app.winget_data?.package_identifier || app.display_name_normalized,
                                      fromDiscover: true
                                    }
                                  });
                                }}
                                className="text-green-400 border-green-500 hover:bg-green-900/20 text-xs"
                                data-testid={`package-upgrade-btn-${idx}`}
                                disabled={!canCreatePackages}
                                title={!canCreatePackages ? "You don't have permission to create packages" : ""}
                              >
                                <Package className="h-3 w-3 mr-1" />
                                Package
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-[rgba(229,239,250,0.1)]">
                      <p className="text-sm text-white/90">
                        Showing {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newPage = Math.max(1, page - 1);
                            setPage(newPage);
                            fetchUpgradableApps(newPage);
                          }}
                          disabled={page === 1}
                          data-testid="upgrades-prev-page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-white/90">
                          Page {page} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newPage = Math.min(totalPages, page + 1);
                            setPage(newPage);
                            fetchUpgradableApps(newPage);
                          }}
                          disabled={page === totalPages}
                          data-testid="upgrades-next-page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Device Details Modal */}
        <Dialog open={showDevicesModal} onOpenChange={setShowDevicesModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Device Details: {selectedApp?.display_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Version:</span> {selectedApp?.version || "-"}
                </div>
                <div>
                  <span className="font-medium">Publisher:</span> {selectedApp?.publisher || "-"}
                </div>
                <div>
                  <span className="font-medium">Size:</span> {formatSize(selectedApp?.size_in_byte)}
                </div>
                <div>
                  <span className="font-medium">Device Count:</span> {selectedApp?.device_count || 0}
                </div>
              </div>
              
              {selectedApp?.device_names && selectedApp.device_names.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Installed on Devices:</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {selectedApp.device_names.map((deviceName, idx) => (
                      <div key={idx} className="text-sm bg-gray-700 text-gray-100 px-3 py-1 rounded">
                        {deviceName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* CVE Details Modal */}
        <Dialog open={showCveModal} onOpenChange={setShowCveModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-500" />
                CVE Details - {selectedCveApp?.display_name || selectedCveApp?.display_name_normalized}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* App Info */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white/90">Application</p>
                    <p className="text-white font-medium">{selectedCveApp?.display_name || selectedCveApp?.display_name_normalized}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/90">Version</p>
                    <p className="text-white font-medium">{selectedCveApp?.version || selectedCveApp?.version_normalized || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/90">Highest Severity</p>
                    <div className="mt-1">{getSeverityBadge(selectedCveApp?.cve_data?.highest_severity)}</div>
                  </div>
                  <div>
                    <p className="text-xs text-white/90">Total CVEs</p>
                    <p className="text-white font-medium text-lg">{selectedCveApp?.cve_data?.cve_count || 0}</p>
                  </div>
                </div>
              </div>

              {/* CVE List */}
              <div>
                <h4 className="font-medium text-white mb-3">Associated CVE Records</h4>
                <div className="space-y-2">
                  {selectedCveApp?.cve_data?.cves?.map((cve, idx) => {
                    const severityColors = getSeverityColor(cve.base_severity);
                    return (
                      <a
                        key={idx}
                        href={`https://www.cve.org/CVERecord?id=${cve.cve_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block bg-gray-800 hover:bg-gray-700 rounded-lg p-4 transition-colors border-l-4 ${severityColors.border}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${severityColors.bg} ${severityColors.text}`}>
                              {cve.base_severity}
                            </span>
                            <span className="text-white font-mono font-medium">{cve.cve_id}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/90 text-sm">Score: {cve.base_score?.toFixed(1) || 'N/A'}</span>
                            <ExternalLink className="h-4 w-4 text-blue-400" />
                          </div>
                        </div>
                        <p className="text-xs text-white/80 mt-2">
                          Click to view full details on cve.org
                        </p>
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Recommendation */}
              {selectedCveApp?.cve_data?.highest_severity && (
                <div className={`rounded-lg p-4 ${
                  selectedCveApp.cve_data.highest_severity === 'CRITICAL' ? 'bg-red-900/30 border border-red-500/50' :
                  selectedCveApp.cve_data.highest_severity === 'HIGH' ? 'bg-orange-900/30 border border-orange-500/50' :
                  'bg-yellow-900/30 border border-yellow-500/50'
                }`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                      selectedCveApp.cve_data.highest_severity === 'CRITICAL' ? 'text-red-400' :
                      selectedCveApp.cve_data.highest_severity === 'HIGH' ? 'text-orange-400' :
                      'text-yellow-400'
                    }`} />
                    <div>
                      <p className="text-white font-medium">Recommendation</p>
                      <p className="text-white/90 text-sm mt-1">
                        {selectedCveApp.cve_data.highest_severity === 'CRITICAL' 
                          ? 'This application has critical vulnerabilities. Immediate update is strongly recommended.'
                          : selectedCveApp.cve_data.highest_severity === 'HIGH'
                          ? 'This application has high-severity vulnerabilities. Update as soon as possible.'
                          : 'This application has known vulnerabilities. Consider updating to a patched version.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Go Live Warning Dialog */}
        {/* Uninstall Only confirmation.
            Creating the package assigns nothing in Intune — the removal only
            reaches devices when the package is deployed to Test and then
            through the phases — but the wording here states plainly what the
            finished package will do, because that is the decision the user is
            actually making. */}
        <Dialog
          open={!!uninstallOnlyTarget}
          onOpenChange={(open) => { if (!open && !uninstallOnlyBusy) setUninstallOnlyTarget(null); }}
        >
          <DialogContent className="max-w-md" data-testid="uninstall-only-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400">
                <Trash2 className="h-5 w-5" />
                Create uninstall-only package
              </DialogTitle>
              <DialogDescription className="text-white/70">
                This builds a package that removes an application. It does not install anything.
              </DialogDescription>
            </DialogHeader>
            {uninstallOnlyTarget && (
              <div className="py-2 space-y-4">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <p className="font-medium text-white">{uninstallOnlyTarget.family_name}</p>
                  <p className="text-xs text-white/60 mt-1">
                    {(uninstallOnlyTarget.publishers && uninstallOnlyTarget.publishers[0]) ||
                      uninstallOnlyTarget.publisher ||
                      "Unknown publisher"}
                    {" · "}
                    {(uninstallOnlyTarget.unique_device_count ||
                      uninstallOnlyTarget.total_device_count ||
                      0).toLocaleString()}{" "}
                    device(s) on the estate
                  </p>
                </div>
                <ul className="text-sm text-white/80 space-y-2 list-disc pl-5">
                  <li>The package has no installer — the install action does nothing.</li>
                  <li>
                    Uninstall scans each machine&apos;s registry for the application and runs its
                    registered silent uninstall, the same logic used for EXE packages today.
                  </li>
                  <li>
                    <strong>Every version</strong> found is removed, not just v
                    {uninstallOnlyTarget.highest_version || "latest"}.
                  </li>
                  <li>
                    When you publish it, ALICE creates an <strong>uninstall</strong> assignment in
                    Intune rather than an install one. The test group accepts device names only.
                  </li>
                </ul>
                <p className="text-xs text-white/50">
                  Nothing is removed yet. Deploy the package to Test from the Packages page when
                  you are ready.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUninstallOnlyTarget(null)}
                disabled={uninstallOnlyBusy}
                className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmUninstallOnly}
                disabled={uninstallOnlyBusy}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                data-testid="confirm-uninstall-only"
              >
                {uninstallOnlyBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Building...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Create package
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showGoLiveWarning} onOpenChange={setShowGoLiveWarning}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-500">
                <AlertTriangle className="h-5 w-5" />
                Pending Tester Approvals
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {pendingGoLiveItem && (
                <>
                  <p className="text-gray-300 mb-4">
                    Not all testers have approved <strong>{pendingGoLiveItem.family_name}</strong> yet.
                  </p>
                  {testApprovals[pendingGoLiveItem.packaging_status?.package_id] && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-green-400">
                            {testApprovals[pendingGoLiveItem.packaging_status?.package_id]?.summary?.approved || 0}
                          </div>
                          <div className="text-xs text-white/90">Approved</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-orange-400">
                            {testApprovals[pendingGoLiveItem.packaging_status?.package_id]?.summary?.pending || 0}
                          </div>
                          <div className="text-xs text-white/90">Pending</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-400">
                            {testApprovals[pendingGoLiveItem.packaging_status?.package_id]?.summary?.rejected || 0}
                          </div>
                          <div className="text-xs text-white/90">Rejected</div>
                        </div>
                      </div>
                      {/* Show individual testers */}
                      {testApprovals[pendingGoLiveItem.packaging_status?.package_id]?.approvals?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[rgba(229,239,250,0.1)] space-y-1">
                          {testApprovals[pendingGoLiveItem.packaging_status?.package_id].approvals.map((a, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-white/90">{a.tester_email}</span>
                              <span className={
                                a.status === 'approved' ? 'text-green-400' :
                                a.status === 'rejected' ? 'text-red-400' :
                                a.status === 'not_notified' ? 'text-gray-400' : 'text-orange-400'
                              }>
                                {a.status === 'not_notified' ? 'Not Notified' : a.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-orange-400 mb-4">
                    ⚠️ Going live without all approvals may result in deploying untested software.
                  </p>
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowGoLiveWarning(false);
                        setPendingGoLiveItem(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-orange-600 hover:bg-orange-700"
                      onClick={handleGoLiveOverride}
                    >
                      Override & Go Live
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default DiscoverPage;
